import {
  AngleIcon,
  BlendingModeIcon,
  BorderAllIcon,
  BorderBottomIcon,
  BorderLeftIcon,
  BorderRightIcon,
  BorderSplitIcon,
  BorderStyleIcon,
  BorderTopIcon,
  CornerBottomLeftIcon,
  CornerBottomRightIcon,
  CornerTopLeftIcon,
  CornerTopRightIcon,
  CursorArrowIcon,
  FontSizeIcon,
  LetterSpacingIcon,
  LineHeightIcon,
  MagicWandIcon,
  ShadowIcon,
} from "@radix-ui/react-icons";
import _ from "lodash";
import {
  ArrowUp,
  ChevronsLeftRightIcon,
  ChevronsRightLeftIcon,
  Maximize2,
  Minimize2,
  MoveHorizontalIcon,
  Scan,
  Type,
} from "lucide-react";
import type React from "react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FixedSizeList as List } from "react-window";
import {
  AlignItemsEndIcon,
  AlignItemsStartIcon,
  AlignLeftIcon,
  ExpandSettingPadding,
  FliPIcon,
  PlusIcon,
  RevertIcon,
  SolidBarIcon,
  SpacingIcon,
} from "@/components/RightStyle/TailwindStylingPanel/components/icons";
import { useTailwindProperties } from "@/hooks/useTailwindProperties";
import { cn } from "@/lib/utils";
import { UpdateClassesChange } from "@/store/zustand/changeLedgerTypes";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import {
  useDesignCommandManager,
  useEditingClassesByCodebaseId,
} from "@/store/zustand/designCommandManagerStore";
import {
  type ClassType,
  type ClassTypeWithPropertyType,
  PropertyType,
  safeExtractValueFromCss,
} from "@/tailwind/tailwindUtils";
import type { JsxElement, JsxElementType } from "@/utils/jsx/jsxElementTypes";
import type { TempoElement } from "@/utils/tempoElement";
import { MaxWidthIcon, MinWidthIcon } from "../Core/Icons";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Textarea } from "../ui/textarea";
import type { CssFieldValue } from ".";
import {
  ClickableIcon,
  type ComboBoxOptionType,
  ComboboxContainer,
  DiscreteInput,
  type DiscreteOption,
  type DropdownOptionType,
} from "./TailwindStylingPanel/components/input";
import ColorInput from "./TailwindStylingPanel/components/input/ColorInput";
import CustomColorPicker from "./TailwindStylingPanel/components/input/CustomColorPicker";
import { VariantDraggableInput as DraggableInput } from "./TailwindStylingPanel/components/input/VariantDraggableInput";
import {
  type ModalState,
  modalTypeToTitle,
  StylingModalType,
} from "./TailwindStylingPanel/components/modals";
import FillModalV2 from "./TailwindStylingPanel/components/modals/FillModalV2";
import {
  areClassesUnchanged,
  calculateNewClasses,
  safeParseCssStringAsInlineStyles,
  sortTailwindColorOptions,
} from "./TailwindStylingPanel/utils";
import { isClassVariant, useNewPropertyState2 } from "./usePropertyStateV2";
import "highlight.js/styles/github.css";
import {
  useCanvasStore,
  useMultiselectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { convertOptionCssVariableToTailwindClass } from "@/tailwind/tailwindUtils";
import { useCanvasId } from "@/utils/utils";
import { useSimpleCanvasStore } from "../SimpleCanvas/simpleCanvasStore";
import { Command, CommandInput, CommandItem } from "../ui/command";
import { CreateComponentSection } from "./CreateComponentSection";
import PropManager from "./PropManager";
import { validHex } from "./TailwindStylingPanel/components/input/color-picker/utils/validate";
import ModalContainer from "./TailwindStylingPanel/components/modals/ModalContainer";
import { SectionHeader } from "./TailwindStylingPanel/components/SectionHeader";
import {
  VariantControl,
  VariantDot,
} from "./TailwindStylingPanel/components/variant/VariantControl";
import AlignField from "./TailwindStylingPanel/fields/Layout/Align";
import AlignChildrenField from "./TailwindStylingPanel/fields/Layout/AlignChildren";
import FlexDirectionWrapField from "./TailwindStylingPanel/fields/Layout/FlexDirectionWrap";
import GapField from "./TailwindStylingPanel/fields/Layout/Gap";
import WrapContentField from "./TailwindStylingPanel/fields/Layout/WrapContent";
import TextAlignField from "./TailwindStylingPanel/fields/Typography/TextAlign";
import TextDecorationField from "./TailwindStylingPanel/fields/Typography/TextDecoration";
import { applyTailwindChangeToClasses } from "./TailwindStylingPanel/utils/applyChanges";

type EnhancedClassType = ClassType & {
  custom?: boolean;
  score: number;
};

const Row = ({
  index,
  style,
  filteredProperties,
  handleSelect,
  isSelected,
}: {
  index: number;
  style: React.CSSProperties;
  filteredProperties: EnhancedClassType[];
  handleSelect: (cls: EnhancedClassType) => void;
  isSelected: boolean;
}) => {
  const cls = filteredProperties[index];

  return (
    <CommandItem
      key={`item-${index}`}
      value={`item-${index}-${cls.tailwind.replace(/[^a-zA-Z0-9-_]/g, "")}`}
      onSelect={() => {
        handleSelect(cls);
      }}
      className={cn("h-7!", isSelected && "bg-accent")}
      style={style}
    >
      <div className="h-full w-full">
        <div className="h-full w-full flex flex-row justify-between items-center gap-0.5">
          <div className="font-medium shrink-0 nowrap space-x-2">
            <span className="text-text-primary text-xs font-normal">
              {cls.tailwind}
            </span>
          </div>
          <div className="flex shrink-0 flex-col">
            <div className="flex flex-col h-full">
              <div className="leading-tight text-right shrink w-fit text-text-tertiary text-xs font-normal line-clamp-1">
                {cls.css
                  .replace(/"/g, "")
                  .split(";")
                  // only take the first 2
                  .slice(0, 2)
                  .join(";")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </CommandItem>
  );
};

enum FlexGrowShrinkOptions {
  Grow = "Grow",
  Shrink = "Shrink",
  NoGrowShrink = "No Shrink / Grow",
}

const horizontalFlexGrowShrinkOptions: DropdownOptionType[] = [
  {
    label: "Grow if possible",
    value: FlexGrowShrinkOptions.Grow,
    icon: <ChevronsLeftRightIcon width={15} height={15} />,
  },
  {
    label: "Shrink if needed",
    value: FlexGrowShrinkOptions.Shrink,
    icon: <ChevronsRightLeftIcon width={15} height={15} />,
  },
  {
    label: "Don't shrink or grow",
    value: FlexGrowShrinkOptions.NoGrowShrink,
    icon: <MoveHorizontalIcon width={15} height={15} />,
  },
];

const verticalFlexGrowShrinkOptions: DropdownOptionType[] = [
  {
    label: "Grow if possible",
    value: FlexGrowShrinkOptions.Grow,
    icon: (
      <ChevronsLeftRightIcon width={15} height={15} className="rotate-90" />
    ),
  },
  {
    label: "Shrink if needed",
    value: FlexGrowShrinkOptions.Shrink,
    icon: <ChevronsRightLeftIcon width={15} height={15} />,
  },
  {
    label: "Don't shrink or grow",
    value: FlexGrowShrinkOptions.NoGrowShrink,
    icon: <MoveHorizontalIcon width={15} height={15} className="rotate-90" />,
  },
];

const BORDER_WIDTH_OPTIONS: DiscreteOption[] = [
  {
    key: "all",
    option: {
      css: "",
      tailwind: "",
    },
    labelContent: <BorderAllIcon />,
    tooltipContent: "All",
  },
  {
    key: "individual",
    option: {
      css: "",
      tailwind: "",
    },
    labelContent: <BorderSplitIcon />,
    tooltipContent: "Individual",
  },
];

const DebugInformation = ({
  cssValues,
  selectedElement,
  selectedTreeElement,
  elementClassList,
  parentCssEvals,
}: {
  cssValues: { [cssName: string]: CssFieldValue };
  selectedElement: TempoElement | null;
  selectedTreeElement: JsxElement | null;
  elementClassList: string[];
  parentCssEvals: { [cssName: string]: string };
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const filteredCssValues = Object.entries(cssValues || {}).filter(([key]) =>
    key.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="border-t border-gray-700 pt-4 mt-4">
      <h3 className="font-semibold text-lg mb-3 text-gray-100">
        Debug Information
      </h3>

      <div className="border-b border-gray-700 pb-3 mb-3">
        <h4 className="font-semibold text-md mb-2 text-gray-200">
          Selected Element
        </h4>
        <div className="pl-2">
          <div className="flex items-center mb-1 text-sm">
            <span className="w-1/3 text-gray-400">Codebase ID:</span>
            <span className="w-2/3 font-medium text-gray-200">
              {selectedElement?.codebaseId}
            </span>
          </div>
          <div className="flex items-center text-sm">
            <span className="w-1/3 text-gray-400">Storyboard ID:</span>
            <span className="w-2/3 font-medium text-gray-200">
              {selectedElement?.storyboardId}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 w-full mb-3">
        <h4 className="font-semibold text-md mb-2 text-gray-200">Class List</h4>
        <div className="pl-2 wrap-break-word text-xs bg-gray-700 p-2 rounded text-gray-300 font-mono">
          {elementClassList?.join(" ")}
        </div>
      </div>

      <div className="flex flex-col gap-2 w-full mb-3">
        <h4 className="font-semibold text-md mb-2 text-gray-200">
          Parent CSS Evaluations
        </h4>
        <div className="pl-2 text-xs bg-gray-700 p-2 rounded text-gray-300 font-mono">
          {parentCssEvals &&
            Object.entries(parentCssEvals).map(([key, value]) => (
              <div key={key} className="mb-1">
                <span className="text-gray-400">{key}:</span> {value}
              </div>
            ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 w-full mb-3">
        <h4 className="font-semibold text-md mb-2 text-gray-200">
          Selected Tree Element
        </h4>
        <div className="pl-2 text-xs bg-gray-700 p-2 rounded text-gray-300 font-mono">
          {selectedTreeElement &&
            Object.entries(selectedTreeElement).map(([key, value]) => (
              <div key={key} className="mb-1">
                <span className="text-gray-400">{key}:</span>{" "}
                {JSON.stringify(value)}
              </div>
            ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 w-full">
        <h4 className="font-semibold text-md mb-2 text-gray-200">CSS Values</h4>
        <input
          type="text"
          placeholder="Search CSS values..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mb-2 p-2 bg-gray-700 text-gray-200 rounded"
        />
        <div className="pl-2 text-xs bg-gray-700 p-2 rounded text-gray-300 font-mono">
          {filteredCssValues.map(([key, value]) => (
            <div key={key} className="mb-2">
              <span className="text-gray-400">{key}:</span>
              <div className="pl-2">
                <div>Evaluated Value: {value.evaluatedValue}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

type Props = {
  parentCssEvals?: { [cssName: string]: string };
  classList: string[];
  selectedCodebaseId: string;
  selectedTopStoryboardId?: string;
  treeElementType: JsxElementType;
  cssValues: { [cssName: string]: CssFieldValue };
};

const updateInstantForPropertyThrottled = _.throttle(
  ({
    propertyType,
    option,
    setTemporaryDraggingValue,
    variant,
    elementClasses,
    tailwindClassesMap,
    selectedCodebaseId,
    selectedTopStoryboardId,
  }: {
    propertyType: PropertyType;
    option: ComboBoxOptionType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
    elementClasses: string[];
    tailwindClassesMap: Record<string, ClassTypeWithPropertyType>;
    selectedCodebaseId: string;
    selectedTopStoryboardId?: string;
  }) => {
    const newClasses = calculateNewClasses({
      option,
      propertyType,
      oldClasses: elementClasses,
      tailwindClassesMap,
      variant,
    });

    let propertyValue = safeExtractValueFromCss(option);

    if (propertyType === PropertyType.ROTATE) {
      propertyValue = propertyValue.match(/rotate\(([^)]+)\)/)?.[1] || "0deg";
    }

    if (
      (selectedTopStoryboardId || selectedCodebaseId) &&
      (propertyType === PropertyType.WIDTH ||
        propertyType === PropertyType.HEIGHT)
    ) {
      const valueDigitsOnly = Number.parseFloat(
        propertyValue.replace(/[^\d.]/g, ""),
      );
      const updateDict =
        propertyType === PropertyType.WIDTH
          ? { width: valueDigitsOnly }
          : { height: valueDigitsOnly };

      const resizeOutlineRef =
        useSimpleCanvasStore.getState().itemToResizableOutlineRef[
          selectedTopStoryboardId || selectedCodebaseId
        ];
      resizeOutlineRef?.applyInstantResize(updateDict);
      return;
    }

    const change: UpdateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedCodebaseId,
      oldClasses: elementClasses,
      newClasses: newClasses,
      involvedClasses: [
        {
          ...option,
          variant,
        },
      ],
      isInstantChangeOnly: true,
    });

    if (setTemporaryDraggingValue) {
      setTemporaryDraggingValue(propertyValue);
    }

    useDesignCommandManager.getState().sendInstantChange(change);
  },
  25,
  {
    leading: true,
    trailing: false,
  },
);

export const TailwindStylingPanel = ({
  parentCssEvals,
  classList,
  selectedCodebaseId,
  selectedTopStoryboardId,
  treeElementType,
  cssValues,
}: Props) => {
  const { multiselectedElementKeys } = useMultiselectedNavTreeNode();

  let errorText: string | undefined;
  if (!selectedCodebaseId) {
    errorText = "Can't find element in codebase, please select another element";
  } else if (multiselectedElementKeys?.length) {
    errorText = "Can't edit styling for multiple elements at once";
  }

  if (errorText) {
    return (
      <div className="px-4 py-2 flex flex-row justify-center items-center">
        <div className="absolute top-1/3 flex flex-col justify-center border-dashed border border-secondary-600 p-4 m-2">
          <div className="flex flex-row text-secondary-900 items-center justify-center space-x-2">
            <CursorArrowIcon className="size-3 shrink-0" />
            <div className="text-secondary-900 text-2xs font-medium">
              {errorText}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <TailwindStylingPanelContents
      parentCssEvals={parentCssEvals}
      classList={classList}
      selectedCodebaseId={selectedCodebaseId}
      selectedTopStoryboardId={selectedTopStoryboardId}
      cssValues={cssValues}
      treeElementType={treeElementType}
    />
  );
};

const TailwindStylingPanelContents = ({
  parentCssEvals,
  classList,
  selectedCodebaseId,
  selectedTopStoryboardId,
  treeElementType,
  cssValues,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const borderModalRef = useRef<HTMLDivElement>(null);
  const fillModalRef = useRef<HTMLDivElement>(null);

  const { classes: elementClasses } = useEditingClassesByCodebaseId(
    classList,
    selectedCodebaseId,
  );

  const [manualEdittingClasses, setManualEdittingClasses] = useState<string>(
    elementClasses.join(" "),
  );

  useEffect(() => {
    const newClasses = elementClasses.join(" ");
    setManualEdittingClasses(newClasses);
    setPrevManualClasses(newClasses);
  }, [elementClasses]);

  const [modal, setModal] = useState<ModalState>({
    type: StylingModalType.None,
  });

  const tailwindClassesMap = useCodebaseStore(
    (state) => state.tailwindClassesMap,
  );
  const tailwindClasses = useCodebaseStore((state) => state.tailwindClasses);

  const textColorModalRef = useRef<HTMLDivElement>(null);

  const buildFontWeightColorPreview = (
    option: ComboBoxOptionType,
  ): ReactNode => {
    const styles = safeParseCssStringAsInlineStyles(option.css);
    return (
      <div className="w-full h-full" style={styles}>
        Tt
      </div>
    );
  };

  const buildTextColorPreview = (option: ComboBoxOptionType): ReactNode => {
    const styles = safeParseCssStringAsInlineStyles(option.css);
    return (
      <div className="w-full h-full font-bold" style={styles}>
        Aa
      </div>
    );
  };

  const lineHeightProperties = useTailwindProperties(PropertyType.LINE_HEIGHT);
  const lineHeightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.LINE_HEIGHT,
    cssValues,
  });

  const letterSpacingProperties = useTailwindProperties(
    PropertyType.LETTER_SPACING,
  );
  const letterSpacingState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.LETTER_SPACING,
    cssValues,
  });

  const textColorProperties = useTailwindProperties(PropertyType.TEXT_COLOR);
  const textColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TEXT_COLOR,
    cssValues,
  });
  const textColorDisplayHex = useMemo(() => {
    if (validHex(textColorState.displayValue)) {
      return textColorState.displayValue;
    }
    if (validHex(textColorState.propertyValue)) {
      return textColorState.propertyValue;
    }
    return textColorState.cssValue;
  }, [textColorState]);

  const fontSizeProperties = useTailwindProperties(PropertyType.FONT_SIZE);
  const fontSizeState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FONT_SIZE,
    cssValues,
  });

  const widthProperties = useTailwindProperties(PropertyType.WIDTH);
  const widthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.WIDTH,
    cssValues,
  });

  const minWidthProperties = useTailwindProperties(PropertyType.MIN_WIDTH);
  const minWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MIN_WIDTH,
    cssValues,
  });

  const maxWidthProperties = useTailwindProperties(PropertyType.MAX_WIDTH);
  const maxWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MAX_WIDTH,
    cssValues,
  });

  const heightProperties = useTailwindProperties(PropertyType.HEIGHT);
  const heightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.HEIGHT,
    cssValues,
  });

  const minHeightProperties = useTailwindProperties(PropertyType.MIN_HEIGHT);
  const minHeightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MIN_HEIGHT,
    cssValues,
  });

  const maxHeightProperties = useTailwindProperties(PropertyType.MAX_HEIGHT);
  const maxHeightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MAX_HEIGHT,
    cssValues,
  });

  const paddingHorizontalProperties = useTailwindProperties(
    PropertyType.PADDING_HORIZONTAL,
  );
  const paddingHorizontalState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.PADDING_HORIZONTAL,
    cssValues,
  });

  const paddingVerticalProperties = useTailwindProperties(
    PropertyType.PADDING_VERTICAL,
  );
  const paddingVerticalState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.PADDING_VERTICAL,
    cssValues,
  });

  const paddingLeftProperties = useTailwindProperties(
    PropertyType.PADDING_LEFT,
  );
  const paddingLeftState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.PADDING_LEFT,
    cssValues,
  });

  const paddingRightProperties = useTailwindProperties(
    PropertyType.PADDING_RIGHT,
  );
  const paddingRightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.PADDING_RIGHT,
    cssValues,
  });

  const paddingTopProperties = useTailwindProperties(PropertyType.PADDING_TOP);
  const paddingTopState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.PADDING_TOP,
    cssValues,
  });

  const paddingBottomProperties = useTailwindProperties(
    PropertyType.PADDING_BOTTOM,
  );
  const paddingBottomState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.PADDING_BOTTOM,
    cssValues,
  });

  const marginHorizontalProperties = useTailwindProperties(
    PropertyType.MARGIN_HORIZONTAL,
  );
  const marginHorizontalState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MARGIN_HORIZONTAL,
    cssValues,
  });

  const marginVerticalProperties = useTailwindProperties(
    PropertyType.MARGIN_VERTICAL,
  );
  const marginVerticalState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MARGIN_VERTICAL,
    cssValues,
  });

  const marginBottomProperties = useTailwindProperties(
    PropertyType.MARGIN_BOTTOM,
  );
  const marginBottomState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MARGIN_BOTTOM,
    cssValues,
  });

  const marginTopProperties = useTailwindProperties(PropertyType.MARGIN_TOP);
  const marginTopState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MARGIN_TOP,
    cssValues,
  });

  const marginLeftProperties = useTailwindProperties(PropertyType.MARGIN_LEFT);
  const marginLeftState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MARGIN_LEFT,
    cssValues,
  });

  const marginRightProperties = useTailwindProperties(
    PropertyType.MARGIN_RIGHT,
  );
  const marginRightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.MARGIN_RIGHT,
    cssValues,
  });

  const positionProperties = useTailwindProperties(PropertyType.POSITION);
  const positionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.POSITION,
    cssValues,
  });

  const topProperties = useTailwindProperties(PropertyType.TOP);
  const topState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TOP,
    cssValues,
  });

  const leftProperties = useTailwindProperties(PropertyType.LEFT);
  const leftState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.LEFT,
    cssValues,
  });

  const rightProperties = useTailwindProperties(PropertyType.RIGHT);
  const rightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.RIGHT,
    cssValues,
  });

  const bottomProperties = useTailwindProperties(PropertyType.BOTTOM);
  const bottomState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BOTTOM,
    cssValues,
  });

  const fontWeightProperties = useTailwindProperties(PropertyType.FONT_WEIGHT);
  const fontWeightState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FONT_WEIGHT,
    cssValues,
  });

  const textAlignState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TEXT_ALIGN,
    cssValues,
  });

  const fontFamilyProperties = useTailwindProperties(PropertyType.FONT_FAMILY);
  const fontFamilyState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FONT_FAMILY,
    cssValues,
  });

  const textDecorationLineProperties = useTailwindProperties(
    PropertyType.TEXT_DECORATION_LINE,
  );
  const textDecorationLineState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TEXT_DECORATION_LINE,
    cssValues,
  });

  const fontStyleProperties = useTailwindProperties(PropertyType.FONT_STYLE);
  const fontStyleState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FONT_STYLE,
    cssValues,
  });

  const displayProperties = useTailwindProperties(PropertyType.DISPLAY);
  const displayState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.DISPLAY,
    cssValues,
  });

  const flexDirectionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FLEX_DIRECTION,
    cssValues,
  });

  const justifyContentState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.JUSTIFY_CONTENT,
    cssValues,
  });

  const alignItemsState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.ALIGN_ITEMS,
    cssValues,
  });

  const columnGapState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.COLUMN_GAP,
    cssValues,
  });

  const rowGapProperties = useTailwindProperties(PropertyType.ROW_GAP);
  const rowGapState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.ROW_GAP,
    cssValues,
  });

  const flexWrapState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FLEX_WRAP,
    cssValues,
  });

  const alignContentState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.ALIGN_CONTENT,
    cssValues,
  });

  const opacityProperties = useTailwindProperties(PropertyType.OPACITY);
  const opacityState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.OPACITY,
    cssValues,
  });

  const borderRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_RADIUS,
  );
  const borderRadiusState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_RADIUS,
    cssValues,
  });
  const topLeftRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_TOP_LEFT_RADIUS,
  );
  const topLeftRadiusState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_TOP_LEFT_RADIUS,
    cssValues,
  });

  const topRightRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_TOP_RIGHT_RADIUS,
  );
  const topRightRadiusState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_TOP_RIGHT_RADIUS,
    cssValues,
  });

  const bottomRightRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_BOTTOM_RIGHT_RADIUS,
  );
  const bottomRightRadiusState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_BOTTOM_RIGHT_RADIUS,
    cssValues,
  });

  const bottomLeftRadiusProperties = useTailwindProperties(
    PropertyType.BORDER_BOTTOM_LEFT_RADIUS,
  );
  const bottomLeftRadiusState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_BOTTOM_LEFT_RADIUS,
    cssValues,
  });
  const rotationProperties = useTailwindProperties(PropertyType.ROTATE);
  const rotationState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.ROTATE,
    cssValues,
  });

  const borderColorProperties = useTailwindProperties(
    PropertyType.BORDER_COLOR,
  );
  const borderColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_COLOR,
    cssValues,
  });

  const borderStyleProperties = useTailwindProperties(
    PropertyType.BORDER_STYLE,
  );
  const borderStyleState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_STYLE,
    cssValues,
  });

  const borderWidthProperties = useTailwindProperties(
    PropertyType.BORDER_WIDTH,
  );
  const borderWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_WIDTH,
    cssValues,
  });

  const borderLeftWidthProperties = useTailwindProperties(
    PropertyType.BORDER_LEFT_WIDTH,
  );
  const borderLeftWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_LEFT_WIDTH,
    cssValues,
  });

  const borderRightWidthProperties = useTailwindProperties(
    PropertyType.BORDER_RIGHT_WIDTH,
  );
  const borderRightWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_RIGHT_WIDTH,
    cssValues,
  });
  const borderTopWidthProperties = useTailwindProperties(
    PropertyType.BORDER_TOP_WIDTH,
  );
  const borderTopWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_TOP_WIDTH,
    cssValues,
  });

  const borderBottomWidthProperties = useTailwindProperties(
    PropertyType.BORDER_BOTTOM_WIDTH,
  );
  const borderBottomWidthState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BORDER_BOTTOM_WIDTH,
    cssValues,
  });

  const backgroundColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BACKGROUND_COLOR,
    cssValues,
  });

  const backgroundImageState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BACKGROUND_IMAGE,
    cssValues,
  });

  const animationProperties = useTailwindProperties(PropertyType.ANIMATION);
  const animationState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.ANIMATION,
    cssValues,
  });

  const boxShadowProperties = useTailwindProperties(PropertyType.BOX_SHADOW);
  const boxShadowState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BOX_SHADOW,
    cssValues,
  });

  const blurProperties = useTailwindProperties(PropertyType.BLUR);
  const blurState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BLUR,
    cssValues,
  });

  const flexGrowProperties = useTailwindProperties(PropertyType.FLEX_GROW);
  const flexGrowState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FLEX_GROW,
    cssValues,
  });

  const flexShrinkProperties = useTailwindProperties(PropertyType.FLEX_SHRINK);
  const flexShrinkState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FLEX_SHRINK,
    cssValues,
  });

  const [showSizingSection, setShowSizingSection] = useState(false);
  const [userOpenedSizingSection, setUserOpenedSizingSection] = useState(false);
  const [showMinWidth, setShowMinWidth] = useState(false);
  const [showMaxWidth, setShowMaxWidth] = useState(false);
  const [showMinHeight, setShowMinHeight] = useState(false);
  const [showMaxHeight, setShowMaxHeight] = useState(false);
  const [showSpacingSection, setShowSpacingSection] = useState(false);
  const [layoutExpanded, setLayoutExpanded] = useState(false);
  const [paddingExpanded, setPaddingExpanded] = useState(false);
  const [paddingExpandEnabled, setPaddingExpandEnabled] = useState(true);
  const [marginExpanded, setMarginExpanded] = useState(false);
  const [marginExpandEnabled, setMarginExpandEnabled] = useState(true);
  const [showPositionSection, setShowPositionSection] = useState(false);
  const [showTextSection, setShowTextSection] = useState(false);
  const [showStyleSection, setShowStyleSection] = useState(false);
  const [radiusExpanded, setRadiusExpanded] = useState(false);
  const [activeRadius, setActiveRadius] = useState(0);
  const [borderWidthExpanded, setBorderWidthExpanded] = useState(false);
  const [borderSet, setBorderSet] = useState(false);
  const [fillSet, setFillSet] = useState(false);
  const [showEffectSection, setShowEffectSection] = useState(false);

  const growOrShrinkSet = flexGrowState.isSet || flexShrinkState.isSet;
  const showFlexGrowShrinkSection = growOrShrinkSet;
  const parentFlexDirection = parentCssEvals?.["flex-direction"] || "row";

  const [flexGrowShrink, setFlexGrowShrink] = useState("");

  // For adding classes panel
  const [addClassesOpen, setAddClassessOpen] = useState(false);
  const [addClassOpen, setAddClassOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isAutoSearching, setIsAutoSearching] = useState(false);
  const [prevManualClasses, setPrevManualClasses] = useState<string>(
    elementClasses.join(" "),
  );
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [justSelectedClass, setJustSelectedClass] = useState<boolean>(false);

  const closeAddClassPopover = () => {
    setAddClassOpen(false);
    setIsAutoSearching(false);
    setAutoSearchTerm("");
    setSearchTerm("");
    setCurrentWord("");
    setSelectedIndex(0);
  };

  const startEditingManualClasses = () => {
    setIsFocused(true);
  };

  const stopEditingManualClasses = () => {
    setIsFocused(false);
  };

  const listRef = useRef<List | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentWord, setCurrentWord] = useState("");
  const [autoSearchTerm, setAutoSearchTerm] = useState<string>("");

  const handleSearch = (value: string) => {
    if (searchTerm === value) {
      return;
    }

    setSearchTerm(value);
    const parts = value.split(" ");
    setCurrentWord(parts[parts.length - 1]);
    if (listRef.current) {
      listRef.current.scrollTo(0);
    }
  };

  // Effect to detect when user is typing a new class
  useEffect(() => {
    if (!isFocused) return;

    // Only proceed if the text has actually changed (user is typing)
    if (manualEdittingClasses === prevManualClasses) return;

    // If we just selected a class, skip this update to prevent reopening
    if (justSelectedClass) {
      setJustSelectedClass(false);
      setPrevManualClasses(manualEdittingClasses);
      return;
    }

    const classes = manualEdittingClasses.trim();
    const prevClasses = prevManualClasses.trim();

    // Check if user is adding characters (typing forward)
    const isTypingForward = classes.length > prevClasses.length;

    setPrevManualClasses(manualEdittingClasses);

    const lastChar = classes[classes.length - 1];
    const hasTrailingSpace = lastChar === " ";

    // Get the last word being typed
    const parts = classes.split(" ");
    const lastClass = parts[parts.length - 1];

    // Check if we should show the dropdown
    if (!hasTrailingSpace && lastClass && lastClass.length > 0) {
      // User is actively typing a class (no trailing space)

      if (!isAutoSearching) {
        // Only open if we're starting fresh typing
        const prevParts = prevClasses.split(" ");
        const prevLastClass = prevParts[prevParts.length - 1] || "";

        // Check if this is a new word (after a space) or continuation
        const isNewWord = prevClasses.endsWith(" ") || prevClasses === "";
        const isContinuation =
          isTypingForward &&
          lastClass.startsWith(prevLastClass) &&
          lastClass.length > prevLastClass.length;

        if (isNewWord || isContinuation) {
          setAddClassOpen(true);
          setIsAutoSearching(true);
        }
      }

      // Update search regardless of whether we just opened or not
      if (isAutoSearching || (!isAutoSearching && isTypingForward)) {
        setAutoSearchTerm(lastClass);
        setSearchTerm(lastClass);
        setCurrentWord(lastClass);
        setSelectedIndex(0); // Reset selection when search changes
        if (listRef.current) {
          listRef.current.scrollTo(0);
        }
      }
    } else {
      // Close auto-search if there's a trailing space or empty last class
      if (isAutoSearching) {
        closeAddClassPopover();
      }
    }
  }, [
    manualEdittingClasses,
    isFocused,
    prevManualClasses,
    isAutoSearching,
    justSelectedClass,
  ]);

  const filteredProperties = useMemo(() => {
    const properties: EnhancedClassType[] = (tailwindClasses || [])
      .map((cls) => {
        let score = 0;

        if (!currentWord) {
          score = 1; // So that all items are considered when there's no currentWord
        } else {
          const term = currentWord.toLowerCase();

          if (cls.tailwind.toLowerCase() === term) {
            score += 10;
          } else if (cls.tailwind.toLowerCase().startsWith(term)) {
            score += 5;
          } else if (cls.tailwind.toLowerCase().includes(term)) {
            score += 3;
          }

          if (cls.css.toLowerCase().includes(term)) {
            score += 1;
          }
        }

        return { ...cls, score };
      })
      .sort((a, b) => b.score - a.score) // Sort items with higher scores first
      .filter((item) => item.score > 0); // Filter out non-matching items

    return properties;
  }, [currentWord, tailwindClasses]);

  const handleFlexGrowShrinkDropdownSelected = (option: DropdownOptionType) => {
    const value: FlexGrowShrinkOptions = option.value;

    setFlexGrowShrink(option.value);

    const oldClasses = elementClasses;
    let newClasses = [...oldClasses];
    const involvedClasses: ClassTypeWithPropertyType[] = [];

    switch (value) {
      case FlexGrowShrinkOptions.Grow:
        newClasses = newClasses.filter((cls) => cls !== "shrink");
        newClasses.push("grow");
        involvedClasses.push({
          tailwind: "grow",
          css: "flex-grow: 1",
          propertyType: PropertyType.FLEX_GROW,
        });
        break;
      case FlexGrowShrinkOptions.Shrink:
        newClasses = newClasses.filter((cls) => cls !== "grow");
        newClasses.push("shrink");
        involvedClasses.push({
          tailwind: "shrink",
          css: "flex-shrink: 1",
          propertyType: PropertyType.FLEX_SHRINK,
        });
        break;
      case FlexGrowShrinkOptions.NoGrowShrink:
        newClasses = newClasses.filter(
          (cls) => cls !== "grow" && cls !== "shrink",
        );
        break;
    }

    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedCodebaseId,
      newClasses: newClasses,
      oldClasses: elementClasses,
      involvedClasses: involvedClasses,
      isInstantChangeOnly: false,
    });

    useDesignCommandManager.getState().executeCommand(updateClassesChange);
  };

  useEffect(() => {
    setShowEffectSection(
      animationState.isSet || boxShadowState.isSet || blurState.isSet,
    );
  }, [animationState.isSet, boxShadowState.isSet, blurState.isSet]);

  const backgroundImageEvaluatedValue =
    cssValues["background-image"]?.evaluatedValue;
  useEffect(() => {
    setFillSet(
      backgroundColorState.isSet ||
        backgroundImageState.isSet ||
        (typeof backgroundImageEvaluatedValue === "string" &&
          backgroundImageEvaluatedValue.includes("url")),
    );
  }, [
    backgroundImageState.isSet,
    backgroundColorState.isSet,
    backgroundImageEvaluatedValue,
  ]);

  useEffect(() => {
    setBorderSet(
      borderColorState.isSet ||
        borderWidthState.isSet ||
        borderStyleState.isSet,
    );
  }, [borderColorState.isSet, borderWidthState.isSet, borderStyleState.isSet]);

  const isFlex =
    displayState.propertyValue === "flex" ||
    cssValues?.display?.evaluatedValue === "flex";

  useEffect(() => {
    if (isFlex) {
      setLayoutExpanded(true);
    } else {
      setLayoutExpanded(false);
    }
  }, [isFlex]);

  useEffect(() => {
    // Check if there's a background image or gradient in the CSS values
    const hasBackgroundImageOrGradient =
      typeof backgroundImageEvaluatedValue === "string" &&
      (backgroundImageEvaluatedValue.includes("url") ||
        backgroundImageEvaluatedValue.includes("gradient"));

    setShowStyleSection(
      opacityState.isSet ||
        backgroundColorState.isSet ||
        backgroundImageState.isSet ||
        hasBackgroundImageOrGradient ||
        borderRadiusState.isSet ||
        rotationState.isSet ||
        borderSet,
    );
  }, [
    opacityState.isSet,
    backgroundColorState.isSet,
    backgroundImageState.isSet,
    backgroundImageEvaluatedValue,
    borderRadiusState.isSet,
    rotationState.isSet,
    borderSet,
  ]);

  useEffect(() => {
    const textPropertiesSet =
      fontWeightState.isSet ||
      lineHeightState.isSet ||
      fontSizeState.isSet ||
      letterSpacingState.isSet ||
      textColorState.isSet ||
      textAlignState.isSet;
    setShowTextSection(textPropertiesSet);
  }, [
    fontWeightState.isSet,
    lineHeightState.isSet,
    fontSizeState.isSet,
    letterSpacingState.isSet,
    textColorState.isSet,
    textAlignState.isSet,
  ]);

  useEffect(() => {
    if (
      marginBottomState.propertyValue !== marginTopState.propertyValue ||
      marginLeftState.propertyValue !== marginRightState.propertyValue
    ) {
      setMarginExpanded(true);
      setMarginExpandEnabled(false);
    } else {
      setMarginExpanded(false);
      setMarginExpandEnabled(true);
    }
  }, [
    marginBottomState.propertyValue,
    marginTopState.propertyValue,
    marginLeftState.propertyValue,
    marginRightState.propertyValue,
  ]);

  useEffect(() => {
    setShowPositionSection(
      leftState.isSet ||
        topState.isSet ||
        rightState.isSet ||
        bottomState.isSet ||
        positionState.isSet,
    );
  }, [
    topState.isSet,
    leftState.isSet,
    rightState.isSet,
    bottomState.isSet,
    positionState.isSet,
  ]);

  useEffect(() => {
    setShowSpacingSection(
      paddingBottomState.isSet ||
        paddingTopState.isSet ||
        paddingLeftState.isSet ||
        paddingRightState.isSet ||
        marginTopState.isSet ||
        marginBottomState.isSet ||
        marginLeftState.isSet ||
        marginRightState.isSet ||
        paddingHorizontalState.isSet ||
        paddingVerticalState.isSet ||
        marginHorizontalState.isSet ||
        marginVerticalState.isSet,
    );
  }, [
    paddingBottomState.isSet,
    paddingTopState.isSet,
    paddingLeftState.isSet,
    paddingRightState.isSet,
    marginTopState.isSet,
    marginBottomState.isSet,
    marginLeftState.isSet,
    marginRightState.isSet,
    paddingHorizontalState.isSet,
    paddingVerticalState.isSet,
    marginHorizontalState.isSet,
    marginVerticalState.isSet,
  ]);

  useEffect(() => {
    if (
      widthState.isSet ||
      heightState.isSet ||
      minWidthState.isSet ||
      maxWidthState.isSet ||
      minHeightState.isSet ||
      maxHeightState.isSet
    ) {
      setShowSizingSection(true);
    } else if (!userOpenedSizingSection) {
      setShowSizingSection(false);
    }
    setShowMinWidth(minWidthState.isSet);
    setShowMaxWidth(maxWidthState.isSet);
    setShowMinHeight(minHeightState.isSet);
    setShowMaxHeight(maxHeightState.isSet);
  }, [
    widthState.isSet,
    heightState.isSet,
    minWidthState.isSet,
    maxWidthState.isSet,
    minHeightState.isSet,
    maxHeightState.isSet,
    userOpenedSizingSection,
  ]);

  useEffect(() => {
    if (
      paddingBottomState.propertyValue !== paddingTopState.propertyValue ||
      paddingLeftState.propertyValue !== paddingRightState.propertyValue
    ) {
      setPaddingExpanded(true);
      setPaddingExpandEnabled(false);
    } else {
      setPaddingExpanded(false);
      setPaddingExpandEnabled(true);
    }
  }, [
    paddingBottomState.propertyValue,
    paddingTopState.propertyValue,
    paddingLeftState.propertyValue,
    paddingRightState.propertyValue,
  ]);

  const pickTailwindClassForProperty = ({
    option,
    propertyType,
    setTemporaryDraggingValue,
    variant,
  }: {
    option: ComboBoxOptionType;
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => {
    const { newClasses, involvedClasses } = applyTailwindChangeToClasses({
      currentElementClasses: elementClasses,
      option,
      propertyType,
      variant,
    });

    if (setTemporaryDraggingValue) {
      setTemporaryDraggingValue(null);
    }

    // Early return if no changes needed
    const classesUnchanged = areClassesUnchanged(newClasses, elementClasses);

    if (classesUnchanged) {
      return;
    }

    // Execute the update through the resizable outline so it follows the same code path
    // But skip resizable outline when variants are involved since it doesn't preserve them
    const shouldUseResizableOutline =
      selectedTopStoryboardId &&
      (propertyType === PropertyType.WIDTH ||
        propertyType === PropertyType.HEIGHT) &&
      (!variant || variant === "default");

    if (shouldUseResizableOutline && selectedTopStoryboardId) {
      const propertyValue = safeExtractValueFromCss(option);
      const valueDigitsOnly = Number.parseInt(
        propertyValue.replace(/\D/g, ""),
        10,
      );

      let updateDict: { width?: number; height?: number };
      if (Number.isNaN(valueDigitsOnly)) {
        if (propertyValue !== "fit-content") return;
        const contentSize = useCanvasStore
          .getState()
          .queryStoryboardContentSize(selectedTopStoryboardId);
        if (!contentSize) return;
        updateDict =
          propertyType === PropertyType.WIDTH
            ? { width: contentSize.width }
            : { height: contentSize.height };
      } else {
        updateDict =
          propertyType === PropertyType.WIDTH
            ? { width: valueDigitsOnly }
            : { height: valueDigitsOnly };
      }

      useSimpleCanvasStore
        .getState()
        .itemToResizableOutlineRef[selectedTopStoryboardId].applyFullResize(
          updateDict,
        );
      return;
    }

    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedCodebaseId,
      newClasses,
      oldClasses: elementClasses,
      involvedClasses,
      isInstantChangeOnly: false,
    });

    useDesignCommandManager.getState().executeCommand(updateClassesChange);
  };

  const handleWidthDropdownSelected = (option: DropdownOptionType) => {
    switch (option.value) {
      case "sizing-fixed": {
        // If currently auto or full, switch to a default fixed value
        if (!option.selected) {
          const defaultOption = { tailwind: "w-8", css: "width: 2rem" };
          pickTailwindClassForProperty({
            option: defaultOption,
            propertyType: PropertyType.WIDTH,
            setTemporaryDraggingValue: widthState.setTemporaryDraggingValue,
          });
        }
        // If already fixed, do nothing
        break;
      }
      case "sizing-auto": {
        const autoOption = { tailwind: "w-fit", css: "width: fit-content" };
        pickTailwindClassForProperty({
          option: autoOption,
          propertyType: PropertyType.WIDTH,
          setTemporaryDraggingValue: widthState.setTemporaryDraggingValue,
        });
        break;
      }
      case "sizing-full": {
        const fullOption = { tailwind: "w-full", css: "width: 100%" };
        pickTailwindClassForProperty({
          option: fullOption,
          propertyType: PropertyType.WIDTH,
          setTemporaryDraggingValue: widthState.setTemporaryDraggingValue,
        });
        break;
      }
      case "minWidth":
        if (showMinWidth) {
          clearTailwindClassForProperty({
            propertyType: PropertyType.MIN_WIDTH,
            setTemporaryDraggingValue: minWidthState.setTemporaryDraggingValue,
          });
          setShowMinWidth(false);
        } else {
          setShowMinWidth(true);
          if (minWidthProperties && minWidthProperties.length > 0) {
            pickTailwindClassForProperty({
              option: minWidthProperties[0],
              propertyType: PropertyType.MIN_WIDTH,
              setTemporaryDraggingValue:
                minWidthState.setTemporaryDraggingValue,
            });
          }
        }
        break;
      case "maxWidth":
        if (showMaxWidth) {
          clearTailwindClassForProperty({
            propertyType: PropertyType.MAX_WIDTH,
            setTemporaryDraggingValue: maxWidthState.setTemporaryDraggingValue,
          });
          setShowMaxWidth(false);
        } else {
          setShowMaxWidth(true);
          if (maxWidthProperties && maxWidthProperties.length > 0) {
            pickTailwindClassForProperty({
              option: maxWidthProperties[0],
              propertyType: PropertyType.MAX_WIDTH,
              setTemporaryDraggingValue:
                maxWidthState.setTemporaryDraggingValue,
            });
          }
        }
        break;
      default:
        console.error("width option not recognized");
        break;
    }
  };

  const handleHeightDropdownSelected = (option: DropdownOptionType) => {
    switch (option.value) {
      case "sizing-fixed": {
        // If currently auto or full, switch to a default fixed value
        if (!option.selected) {
          const defaultOption = { tailwind: "h-8", css: "height: 2rem" };
          pickTailwindClassForProperty({
            option: defaultOption,
            propertyType: PropertyType.HEIGHT,
            setTemporaryDraggingValue: heightState.setTemporaryDraggingValue,
          });
        }
        // If already fixed, do nothing
        break;
      }
      case "sizing-auto": {
        const autoOption = { tailwind: "h-fit", css: "height: fit-content" };
        pickTailwindClassForProperty({
          option: autoOption,
          propertyType: PropertyType.HEIGHT,
          setTemporaryDraggingValue: heightState.setTemporaryDraggingValue,
        });
        break;
      }
      case "sizing-full": {
        const fullOption = { tailwind: "h-full", css: "height: 100%" };
        pickTailwindClassForProperty({
          option: fullOption,
          propertyType: PropertyType.HEIGHT,
          setTemporaryDraggingValue: heightState.setTemporaryDraggingValue,
        });
        break;
      }
      case "minHeight":
        if (showMinHeight) {
          clearTailwindClassForProperty({
            propertyType: PropertyType.MIN_HEIGHT,
            setTemporaryDraggingValue: minHeightState.setTemporaryDraggingValue,
          });
          setShowMinHeight(false);
        } else {
          setShowMinHeight(true);
          if (minHeightProperties && minHeightProperties.length > 0) {
            pickTailwindClassForProperty({
              option: minHeightProperties[0],
              propertyType: PropertyType.MIN_HEIGHT,
              setTemporaryDraggingValue:
                minHeightState.setTemporaryDraggingValue,
            });
          }
        }
        break;
      case "maxHeight":
        if (showMaxHeight) {
          clearTailwindClassForProperty({
            propertyType: PropertyType.MAX_HEIGHT,
            setTemporaryDraggingValue: maxHeightState.setTemporaryDraggingValue,
          });
          setShowMaxHeight(false);
        } else {
          setShowMaxHeight(true);
          if (maxHeightProperties && maxHeightProperties.length > 0) {
            pickTailwindClassForProperty({
              option: maxHeightProperties[0],
              propertyType: PropertyType.MAX_HEIGHT,
              setTemporaryDraggingValue:
                maxHeightState.setTemporaryDraggingValue,
            });
          }
        }
        break;
      default:
        console.error("height option not recognized");
        break;
    }
  };

  const clearTailwindClassForProperty = ({
    propertyType,
    setTemporaryDraggingValue,
    variant,
  }: {
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => {
    // Clear both light and dark variants for color properties
    const isColorProperty = [
      PropertyType.BACKGROUND_COLOR,
      PropertyType.TEXT_COLOR,
      PropertyType.BORDER_COLOR,
    ].includes(propertyType);

    let newClasses = elementClasses;

    if (isColorProperty) {
      // Clear default (light mode) variant
      newClasses = calculateNewClasses({
        option: null,
        propertyType,
        oldClasses: newClasses,
        tailwindClassesMap,
        variant: variant || "default",
      });

      // Clear dark mode variant
      newClasses = calculateNewClasses({
        option: null,
        propertyType,
        oldClasses: newClasses,
        tailwindClassesMap,
        variant: "dark",
      });
    } else {
      // For non-color properties, use the original logic
      newClasses = calculateNewClasses({
        option: null,
        propertyType,
        oldClasses: elementClasses,
        tailwindClassesMap,
        variant,
      });
    }

    if (
      newClasses.length === elementClasses.length &&
      newClasses.every((cls, i) => cls === elementClasses[i])
    ) {
      if (setTemporaryDraggingValue) {
        setTemporaryDraggingValue(null);
      }
      return;
    }
    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedCodebaseId,
      newClasses: newClasses,
      oldClasses: elementClasses,
      involvedClasses: [],
      isInstantChangeOnly: false,
    });
    if (setTemporaryDraggingValue) {
      setTemporaryDraggingValue(null);
    }
    useDesignCommandManager.getState().executeCommand(updateClassesChange);
  };

  const clearTailwindClassForMultipleProperties = ({
    propertyTypes,
    setTemporaryDraggingValue,
    variant,
    thenSetOption,
    thenSetPropertyType,
  }: {
    propertyTypes: PropertyType[];
    setTemporaryDraggingValue?: (value: string | null) => void;
    variant?: string | null;
    thenSetOption?: ComboBoxOptionType;
    thenSetPropertyType?: PropertyType;
  }) => {
    let newClasses = [...elementClasses];

    // Apply each property type removal sequentially
    for (const propertyType of propertyTypes) {
      newClasses = calculateNewClasses({
        option: null,
        propertyType,
        oldClasses: newClasses,
        tailwindClassesMap,
        variant,
      });
    }

    // If we have an option to set after clearing, apply it
    if (thenSetOption && thenSetPropertyType) {
      newClasses = calculateNewClasses({
        option: thenSetOption,
        propertyType: thenSetPropertyType,
        oldClasses: newClasses,
        tailwindClassesMap,
        variant,
      });
    }

    // Check if any changes were made
    if (
      newClasses.length === elementClasses.length &&
      newClasses.every((cls, i) => cls === elementClasses[i])
    ) {
      if (setTemporaryDraggingValue) {
        setTemporaryDraggingValue(null);
      }
      return;
    }

    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedCodebaseId,
      newClasses: newClasses,
      oldClasses: elementClasses,
      involvedClasses: thenSetOption ? [thenSetOption] : [],
      isInstantChangeOnly: false,
    });

    if (setTemporaryDraggingValue) {
      setTemporaryDraggingValue(null);
    }

    useDesignCommandManager.getState().executeCommand(updateClassesChange);
  };

  const elementClassesRef = useRef(elementClasses);
  useEffect(() => {
    elementClassesRef.current = elementClasses;
  }, [elementClasses]);

  const tailwindClassesMapRef = useRef(tailwindClassesMap);
  useEffect(() => {
    tailwindClassesMapRef.current = tailwindClassesMap;
  }, [tailwindClassesMap]);

  const classListRef = useRef(classList);
  useEffect(() => {
    classListRef.current = classList;
  }, [classList]);

  const selectedCodebaseIdRef = useRef(selectedCodebaseId);
  useEffect(() => {
    selectedCodebaseIdRef.current = selectedCodebaseId;
  }, [selectedCodebaseId]);

  const selectedTopStoryboardIdRef = useRef(selectedTopStoryboardId);
  useEffect(() => {
    selectedTopStoryboardIdRef.current = selectedTopStoryboardId;
  }, [selectedTopStoryboardId]);

  const canvasId = useCanvasId();
  const canvasIdRef = useRef(canvasId);
  useEffect(() => {
    canvasIdRef.current = canvasId;
  }, [canvasId]);

  const updateInstantForProperty = useCallback(
    ({
      propertyType,
      option,
      setTemporaryDraggingValue,
      variant,
    }: {
      propertyType: PropertyType;
      option: ComboBoxOptionType;
      setTemporaryDraggingValue: (value: string | null) => void;
      variant?: string | null;
    }) => {
      updateInstantForPropertyThrottled({
        propertyType,
        option,
        setTemporaryDraggingValue,
        variant,
        elementClasses: elementClassesRef.current || [],
        tailwindClassesMap: tailwindClassesMapRef.current || {},
        selectedCodebaseId: selectedCodebaseIdRef.current || "",
        selectedTopStoryboardId: selectedTopStoryboardIdRef.current || "",
      });
    },
    [],
  );

  const applyNewClasses = (value?: string) => {
    const newClasses = (value ?? manualEdittingClasses)
      .split(" ")
      .filter(Boolean);
    const oldClasses = elementClasses;
    if (
      newClasses.length === oldClasses.length &&
      newClasses.every((cls, i) => cls === oldClasses[i])
    ) {
      setManualEdittingClasses(oldClasses.join(" "));
      return;
    }
    const missingClasses = newClasses.filter(
      (cls) => !oldClasses.includes(cls),
    );
    const involvedClasses: {
      tailwind: string;
      css: string;
      variant?: string | null;
    }[] = [];
    for (const cls of missingClasses) {
      if (isClassVariant(cls)) {
        const [variant, actualClassName] = cls.split(":");
        if (tailwindClassesMap[actualClassName]) {
          involvedClasses.push({
            ...tailwindClassesMap[actualClassName],
            variant,
          });
        }
      } else if (tailwindClassesMap[cls]) {
        involvedClasses.push(tailwindClassesMap[cls]);
      }
    }
    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedCodebaseId,
      oldClasses: elementClasses,
      newClasses: newClasses,
      involvedClasses: involvedClasses,
      isInstantChangeOnly: false,
    });
    useDesignCommandManager.getState().executeCommand(updateClassesChange);
    setManualEdittingClasses(newClasses.join(" "));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // If popover is open and auto-searching, select the current item
      if (addClassOpen && isAutoSearching && filteredProperties.length > 0) {
        const selectedClass = filteredProperties[selectedIndex];
        const classes = manualEdittingClasses.trim().split(" ");
        classes[classes.length - 1] = selectedClass.tailwind; // Replace the partial class with the selected one
        const newValue = classes.join(" ") + " "; // Add a space after selection
        setJustSelectedClass(true); // Set flag to prevent reopening
        setManualEdittingClasses(newValue);
        closeAddClassPopover();
        applyNewClasses(newValue.trim()); // Apply without the trailing space
      } else {
        applyNewClasses();
      }
    } else if (e.key === "ArrowDown" && addClassOpen && isAutoSearching) {
      e.preventDefault();
      setSelectedIndex((prev) => {
        const newIndex = Math.min(prev + 1, filteredProperties.length - 1);
        // Scroll to the selected item
        if (listRef.current && newIndex > 0) {
          listRef.current.scrollToItem(newIndex, "smart");
        }
        return newIndex;
      });
    } else if (e.key === "ArrowUp" && addClassOpen && isAutoSearching) {
      e.preventDefault();
      setSelectedIndex((prev) => {
        const newIndex = Math.max(prev - 1, 0);
        // Scroll to the selected item
        if (listRef.current) {
          listRef.current.scrollToItem(newIndex, "smart");
        }
        return newIndex;
      });
    } else if (e.key === "Escape" && addClassOpen) {
      // Close popover on Escape
      closeAddClassPopover();
    }
  };

  // For text color variant modal
  const [textColorVariantModalOpen, setTextColorVariantModalOpen] =
    useState(false);
  const textColorVariantAnchorRef = useRef<HTMLButtonElement>(null);

  // Add state and ref for border color variant modal
  const [borderColorVariantModalOpen, setBorderColorVariantModalOpen] =
    useState(false);
  const borderColorVariantAnchorRef = useRef<HTMLButtonElement>(null);

  // 1. Add state and ref for fill variant modal (near other variant modal states)
  const [fillColorVariantModalOpen, setFillColorVariantModalOpen] =
    useState(false);
  const fillColorVariantAnchorRef = useRef<HTMLButtonElement>(null);

  // Add this with other property arrays at the top
  const backgroundColorProperties = useTailwindProperties(
    PropertyType.BACKGROUND_COLOR,
  );

  // Add with other refs
  const fillModalPopoverRef = useRef<HTMLDivElement>(null);

  // Add state and ref for gradient color stop variant modals/anchors
  const [fromColorVariantModalOpen, setFromColorVariantModalOpen] =
    useState(false);
  const fromColorVariantAnchorRef = useRef<HTMLButtonElement>(null);
  const [viaColorVariantModalOpen, setViaColorVariantModalOpen] =
    useState(false);
  const viaColorVariantAnchorRef = useRef<HTMLButtonElement>(null);
  const [toColorVariantModalOpen, setToColorVariantModalOpen] = useState(false);
  const toColorVariantAnchorRef = useRef<HTMLButtonElement>(null);

  // Add these with other property arrays and states at the top
  const fromColorProperties = useTailwindProperties(PropertyType.FROM_COLOR);
  const fromColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FROM_COLOR,
    cssValues,
  });
  const viaColorProperties = useTailwindProperties(PropertyType.VIA_COLOR);
  const viaColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.VIA_COLOR,
    cssValues,
  });
  const toColorProperties = useTailwindProperties(PropertyType.TO_COLOR);
  const toColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TO_COLOR,
    cssValues,
  });

  const createIcon = (Icon, rotate = false) => (
    <Icon className={rotate ? "rotate-90" : ""} />
  );

  const getLabel = (value, active) => {
    const prefix = value.replace(/([A-Z])/g, " $1").toLowerCase();
    return `${active ? "Remove" : "Add"} ${prefix}...`;
  };

  const widthSizingConfig = [
    {
      value: "minWidth",
      Icon: MinWidthIcon,
      show: showMinWidth,
      rotate: false,
    },
    {
      value: "maxWidth",
      Icon: MaxWidthIcon,
      show: showMaxWidth,
      rotate: false,
    },
  ];

  const heightSizingConfig = [
    {
      value: "minHeight",
      Icon: MinWidthIcon,
      show: showMinHeight,
      rotate: true,
    },
    {
      value: "maxHeight",
      Icon: MaxWidthIcon,
      show: showMaxHeight,
      rotate: true,
    },
  ];

  // Build width dropdown options with sizing modes
  const buildWidthDropdownOptions = () => {
    const options: DropdownOptionType[] = [];

    // Sizing mode options (group 0)
    const isAuto = widthState.className === "w-fit";
    const isFull = widthState.className === "w-full";
    const isFixed = !isAuto && !isFull;
    const displayValue = isFixed ? widthState.displayValue || "32" : "";

    options.push({
      label: `Fixed width${displayValue ? ` (${displayValue})` : ""}`,
      value: "sizing-fixed",
      icon: (
        <img src="/icons/fixed.svg" className="w-4 h-4" alt="Fixed width" />
      ),
      group: 0,
      selected: isFixed,
    });

    options.push({
      label: "Hug contents",
      value: "sizing-auto",
      icon: <Minimize2 className="w-4 h-4" />,
      group: 0,
      selected: isAuto,
    });

    options.push({
      label: "Fill container",
      value: "sizing-full",
      icon: <Maximize2 className="w-4 h-4" />,
      group: 0,
      selected: isFull,
    });

    // Min/Max options (group 1)
    widthSizingConfig.forEach(({ value, Icon, show, rotate }) => {
      options.push({
        label: getLabel(value, show),
        value,
        icon: createIcon(Icon, rotate),
        group: 1,
      });
    });

    return options;
  };

  const widthDropdownOptions = buildWidthDropdownOptions();

  // Build height dropdown options with sizing modes
  const buildHeightDropdownOptions = () => {
    const options: DropdownOptionType[] = [];

    // Sizing mode options (group 0)
    const isAuto = heightState.className === "h-auto";
    const isFull = heightState.className === "h-full";
    const isFixed = !isAuto && !isFull;
    const displayValue = isFixed ? heightState.displayValue || "32" : "";

    options.push({
      label: `Fixed height${displayValue ? ` (${displayValue})` : ""}`,
      value: "sizing-fixed",
      icon: (
        <img src="/icons/fixed.svg" className="w-4 h-4" alt="Fixed height" />
      ),
      group: 0,
      selected: isFixed,
    });

    options.push({
      label: "Hug contents",
      value: "sizing-auto",
      icon: <Minimize2 className="w-4 h-4" />,
      group: 0,
      selected: isAuto,
    });

    options.push({
      label: "Fill container",
      value: "sizing-full",
      icon: <Maximize2 className="w-4 h-4" />,
      group: 0,
      selected: isFull,
    });

    // Min/Max options (group 1)
    heightSizingConfig.forEach(({ value, Icon, show, rotate }) => {
      options.push({
        label: getLabel(value, show),
        value,
        icon: createIcon(Icon, rotate),
        group: 1,
      });
    });

    return options;
  };

  const heightDropdownOptions = buildHeightDropdownOptions();

  const toggleArbitraryClass = (className: string, css: string) => {
    const oldClasses = elementClasses;
    const newClasses = [...oldClasses];
    const idx = newClasses.indexOf(className);
    if (idx !== -1) {
      newClasses.splice(idx, 1);
    } else {
      newClasses.push(className);
    }
    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedCodebaseId,
      oldClasses: oldClasses,
      newClasses: newClasses,
      involvedClasses: [
        {
          tailwind: className,
          css: css,
        },
      ],
      isInstantChangeOnly: false,
    });
    useDesignCommandManager.getState().executeCommand(updateClassesChange);
  };

  const resetRotationAndFlips = () => {
    const oldClasses = elementClasses;
    const newClasses = oldClasses.filter(
      (cls) =>
        !cls.startsWith("rotate-") &&
        cls !== "scale-x-[-1]" &&
        cls !== "scale-y-[-1]",
    );
    if (newClasses.length === oldClasses.length) return;
    const updateClassesChange = new UpdateClassesChange({
      codebaseIdToUpdateClass: selectedCodebaseId,
      oldClasses: oldClasses,
      newClasses: newClasses,
      involvedClasses: [],
      isInstantChangeOnly: false,
    });
    useDesignCommandManager.getState().executeCommand(updateClassesChange);
  };

  const showPropManager = useMemo(() => {
    return treeElementType === "component-instance";
  }, [treeElementType]);

  return (
    // disable browser right click menu for styling panel
    <div
      className="text-xs relative"
      ref={containerRef}
      onContextMenu={(e) => e.preventDefault()}
    >
      <ComboboxContainer>
        <CreateComponentSection selectedCodebaseId={selectedCodebaseId} />

        <SectionHeader
          title="Classes"
          isExpanded={addClassesOpen}
          onToggle={() => setAddClassessOpen((prev) => !prev)}
          isActive={addClassesOpen}
          icon={
            <img
              height={16}
              width={16}
              src="/images/tailwind-logo.svg"
              alt="Add a Tailwind class"
            />
          }
        >
          {/* Popover sizes */}
          {
            <Popover open={addClassOpen}>
              <PopoverTrigger asChild style={{ zIndex: 1000 }}>
                <div
                  className={cn(
                    "relative group pb-6 bg-secondary-n-1000 hover:bg-secondary-n-950 cursor-pointer rounded-md flex flex-row justify-between border-secondary-n-900 items-center border",
                    isFocused && "border-primary-0",
                  )}
                  data-tooltip-content="Add Tailwind class"
                  data-tooltip-id="tooltip"
                  data-tooltip-place="top"
                >
                  <Textarea
                    onKeyDown={handleKeyDown}
                    value={manualEdittingClasses}
                    onFocus={startEditingManualClasses}
                    onBlur={stopEditingManualClasses}
                    placeholder="Search classes"
                    onChange={(e) => setManualEdittingClasses(e.target.value)}
                    className="mb-3 resize-none w-full min-h-[80px] text-xs text-secondary-n-50 "
                  />
                  <div className="flex border border-secondary-n-900 mt-2 items-center gap-1 absolute bg-secondary-n-950 rounded-md bottom-1 right-1 text-secondary-n-50">
                    <ClickableIcon
                      onClick={() => {
                        setAddClassOpen(true);
                        setIsAutoSearching(false);
                        setAutoSearchTerm("");
                        setSearchTerm("");
                        setCurrentWord("");
                        setSelectedIndex(0);
                      }}
                    >
                      <PlusIcon />
                    </ClickableIcon>
                  </div>
                  {isFocused &&
                    manualEdittingClasses.trim() !==
                      elementClasses.join(" ").trim() && (
                      <div className="flex border border-secondary-n-900 items-center gap-1 absolute bg-secondary-n-950 p-1 px-1.5 rounded-md bottom-1 right-9 text-secondary-n-50">
                        Enter to apply <ArrowUp className="w-4 h-4 " />
                      </div>
                    )}
                </div>
              </PopoverTrigger>
              <PopoverContent
                className="h-[400px] max-w-64 z-1004 text-2xs p-0 rounded-xl"
                side="bottom"
                align="start"
                onOpenAutoFocus={(e) => {
                  // Prevent the popover from stealing focus when in auto-search mode
                  if (isAutoSearching) {
                    e.preventDefault();
                  }
                }}
                onInteractOutside={() => {
                  closeAddClassPopover();
                }}
              >
                <Command
                  shouldFilter={false}
                  loop={false}
                  className="p-0 rounded-xl"
                >
                  {!isAutoSearching && (
                    <div className="pb-1 border-secondary-n-900 p-1.5">
                      <CommandInput
                        placeholder="Search for a classes"
                        onValueChange={handleSearch}
                        rootClassName="px-1 py-1 bg-secondary-n-950"
                      />
                    </div>
                  )}
                  <div className="p-1.5">
                    <List
                      id="tailwind-classes-list"
                      ref={listRef}
                      height={40 * 12}
                      itemCount={filteredProperties.length}
                      itemSize={40}
                      width="100%"
                    >
                      {({ index, style }) => (
                        <Row
                          index={index}
                          style={style}
                          filteredProperties={filteredProperties}
                          isSelected={index === selectedIndex}
                          handleSelect={(cls) => {
                            setManualEdittingClasses((current) => {
                              let newValue;
                              if (isAutoSearching) {
                                // Replace the partial class with the selected one
                                const classes = current.trim().split(" ");
                                classes[classes.length - 1] = cls.tailwind;
                                newValue = classes.join(" ") + " "; // Add space after selection
                                setJustSelectedClass(true); // Set flag to prevent reopening
                              } else {
                                newValue = `${current} ${cls.tailwind}`.trim();
                              }

                              if (
                                newValue.trim() !==
                                elementClasses.join(" ").trim()
                              ) {
                                setTimeout(
                                  () => applyNewClasses(newValue.trim()),
                                  0,
                                );
                              }

                              // Close popover and reset all search states
                              closeAddClassPopover();
                              return newValue;
                            });
                          }}
                        />
                      )}
                    </List>
                  </div>
                </Command>
              </PopoverContent>
            </Popover>
          }
        </SectionHeader>
        <hr />
        {showPropManager && selectedCodebaseId && (
          <>
            <PropManager />
            <hr />
          </>
        )}
        <SectionHeader
          title="Position"
          isExpanded={showPositionSection}
          onToggle={() => setShowPositionSection((prev) => !prev)}
          isActive={showPositionSection}
          data-testid="styling-panel-section-position"
        >
          {
            <>
              <DraggableInput
                propertyState={positionState}
                placeholder="Position"
                comboboxOptions={positionProperties}
                onComboboxSelected={(option, variant) => {
                  pickTailwindClassForProperty({
                    option,
                    propertyType: PropertyType.POSITION,
                    setTemporaryDraggingValue:
                      positionState.setTemporaryDraggingValue,
                    variant,
                  });
                }}
                onSliderValueUpdate={(option, variant) => {
                  updateInstantForProperty({
                    propertyType: PropertyType.POSITION,
                    option,
                    setTemporaryDraggingValue:
                      positionState.setTemporaryDraggingValue,
                    variant,
                  });
                }}
                draggable={false}
                onClear={(variant?: string) => {
                  clearTailwindClassForProperty({
                    propertyType: PropertyType.POSITION,
                    setTemporaryDraggingValue:
                      positionState.setTemporaryDraggingValue,
                    variant,
                  });
                }}
                tooltipContent="Position"
                data-testid="styling-panel-position-input"
              />

              {positionState.propertyValue !== "static" &&
                positionState.propertyValue !== "sticky" && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {positionState.propertyValue !== "static" && (
                      <DraggableInput
                        propertyState={topState}
                        labelContent={
                          <AlignItemsEndIcon className="-rotate-90" />
                        }
                        disabled={positionState.propertyValue === "static"}
                        comboboxOptions={topProperties}
                        onComboboxSelected={(option, variant) => {
                          pickTailwindClassForProperty({
                            option,
                            propertyType: PropertyType.TOP,
                            setTemporaryDraggingValue:
                              topState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onSliderValueUpdate={(option, variant) => {
                          updateInstantForProperty({
                            propertyType: PropertyType.TOP,
                            option,
                            setTemporaryDraggingValue:
                              topState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onClear={(variant?: string) => {
                          clearTailwindClassForProperty({
                            propertyType: PropertyType.TOP,
                            setTemporaryDraggingValue:
                              topState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        buildArbitraryClass={(input: string) => {
                          return {
                            tailwind: `top-[${input}]`,
                            css: `top: ${input}`,
                          };
                        }}
                        step={1}
                        fallback={{ number: 0, unit: "px" }}
                        tooltipContent="Top"
                        data-testid="styling-panel-top-input"
                      />
                    )}

                    <DraggableInput
                      propertyState={bottomState}
                      labelContent={
                        <AlignItemsStartIcon className="-rotate-90" />
                      }
                      disabled={positionState.propertyValue === "static"}
                      comboboxOptions={bottomProperties}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BOTTOM,
                          setTemporaryDraggingValue:
                            bottomState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onSliderValueUpdate={(option, variant) => {
                        updateInstantForProperty({
                          propertyType: PropertyType.BOTTOM,
                          option,
                          setTemporaryDraggingValue:
                            bottomState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BOTTOM,
                          setTemporaryDraggingValue:
                            bottomState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      buildArbitraryClass={(input: string) => {
                        return {
                          tailwind: `bottom-[${input}]`,
                          css: `bottom: ${input}`,
                        };
                      }}
                      step={1}
                      fallback={{ number: 0, unit: "px" }}
                      tooltipContent="Bottom"
                      data-testid="styling-panel-bottom-input"
                    />

                    <DraggableInput
                      propertyState={leftState}
                      labelContent={<AlignItemsStartIcon />}
                      comboboxOptions={leftProperties}
                      disabled={positionState.propertyValue === "static"}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.LEFT,
                          setTemporaryDraggingValue:
                            leftState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onSliderValueUpdate={(option, variant) => {
                        updateInstantForProperty({
                          propertyType: PropertyType.LEFT,
                          option,
                          setTemporaryDraggingValue:
                            leftState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.LEFT,
                          setTemporaryDraggingValue:
                            leftState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      buildArbitraryClass={(input: string) => {
                        return {
                          tailwind: `left-[${input}]`,
                          css: `left: ${input}`,
                        };
                      }}
                      step={1}
                      fallback={{ number: 0, unit: "px" }}
                      tooltipContent="Left"
                      data-testid="styling-panel-left-input"
                    />

                    <DraggableInput
                      propertyState={rightState}
                      labelContent={<AlignItemsEndIcon />}
                      comboboxOptions={rightProperties}
                      disabled={positionState.propertyValue === "static"}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.RIGHT,
                          setTemporaryDraggingValue:
                            rightState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onSliderValueUpdate={(option, variant) => {
                        updateInstantForProperty({
                          propertyType: PropertyType.RIGHT,
                          option,
                          setTemporaryDraggingValue:
                            rightState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.RIGHT,
                          setTemporaryDraggingValue:
                            rightState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      buildArbitraryClass={(input: string) => {
                        return {
                          tailwind: `right-[${input}]`,
                          css: `right: ${input}`,
                        };
                      }}
                      step={1}
                      fallback={{ number: 0, unit: "px" }}
                      tooltipContent="Right"
                      data-testid="styling-panel-right-input"
                    />
                  </div>
                )}
            </>
          }
        </SectionHeader>
        <hr />

        <SectionHeader
          title="Size"
          isExpanded={showSizingSection}
          onToggle={() => {
            setShowSizingSection((prev) => {
              const newVal = !prev;
              if (newVal) setUserOpenedSizingSection(true);
              return newVal;
            });
          }}
          isActive={showSizingSection}
        >
          {
            <>
              {/* Flex Grow & Shrink */}
              {showFlexGrowShrinkSection &&
                (parentFlexDirection === "row" ? (
                  <div className="my-2">
                    <div className="text-xs text-secondary-n-400 font-medium mb-2">
                      Grow
                    </div>
                    <DraggableInput
                      propertyState={{
                        propertyValue:
                          parentFlexDirection === "row" ? flexGrowShrink : "",
                        isSet: parentFlexDirection === "row",
                        displayValue:
                          parentFlexDirection === "row" ? flexGrowShrink : "",

                        // TODO: NEED TO DO
                        valueByVariant: {},
                      }}
                      labelContent={
                        horizontalFlexGrowShrinkOptions.find(
                          (option) => option.value === flexGrowShrink,
                        )?.icon || horizontalFlexGrowShrinkOptions[0].icon
                      }
                      dropdownOptions={
                        parentFlexDirection === "row"
                          ? horizontalFlexGrowShrinkOptions
                          : undefined
                      }
                      onDropdownSelected={(option) => {
                        handleFlexGrowShrinkDropdownSelected(option);
                      }}
                      disabled={parentFlexDirection !== "row"}
                      draggable={false}
                      tooltipContent={"Horizontal Resizing"}
                      // TODO: Implement?
                      onClear={() => {}}
                    />
                  </div>
                ) : (
                  <div className="mt-2">
                    <div className="text-xs text-secondary-n-400 font-mediu my-1">
                      Resizing
                    </div>
                    <div>
                      <DraggableInput
                        propertyState={{
                          propertyValue:
                            parentFlexDirection === "column"
                              ? flexGrowShrink
                              : "",
                          isSet: parentFlexDirection === "column",
                          displayValue:
                            parentFlexDirection === "row" ? flexGrowShrink : "",
                          // TODO: NEED TO DO
                          valueByVariant: {},
                        }}
                        labelContent={
                          verticalFlexGrowShrinkOptions.find(
                            (option) => option.value === flexGrowShrink,
                          )?.icon || verticalFlexGrowShrinkOptions[0].icon
                        }
                        dropdownOptions={
                          parentFlexDirection === "column"
                            ? verticalFlexGrowShrinkOptions
                            : undefined
                        }
                        onDropdownSelected={(option) => {
                          handleFlexGrowShrinkDropdownSelected(option);
                        }}
                        disabled={parentFlexDirection !== "column"}
                        draggable={false}
                        tooltipContent={"Vertical Resizing"}
                        // TODO: Implement?
                        onClear={() => {}}
                      />{" "}
                    </div>
                  </div>
                ))}

              {/* Width & Height */}
              <div className="grid gap-y-2">
                <div className="grid grid-cols-2 items-center gap-x-2">
                  <div className="w-full">
                    <DraggableInput
                      propertyState={widthState}
                      labelContent={"W"}
                      comboboxOptions={widthProperties}
                      dropdownOptions={widthDropdownOptions}
                      onDropdownSelected={(option) => {
                        handleWidthDropdownSelected(option);
                      }}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.WIDTH,
                          setTemporaryDraggingValue:
                            widthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onSliderValueUpdate={(option, variant) => {
                        updateInstantForProperty({
                          propertyType: PropertyType.WIDTH,
                          option,
                          setTemporaryDraggingValue:
                            widthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.WIDTH,
                          setTemporaryDraggingValue:
                            widthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      buildArbitraryClass={(input: string) => {
                        return {
                          tailwind: `w-[${input}]`,
                          css: `width: ${input}`,
                        };
                      }}
                      min={0}
                      step={1}
                      tooltipContent="Width"
                    />
                  </div>

                  <div className="w-full">
                    <DraggableInput
                      propertyState={heightState}
                      labelContent={"H"}
                      comboboxOptions={heightProperties}
                      dropdownOptions={heightDropdownOptions}
                      onDropdownSelected={(option) =>
                        handleHeightDropdownSelected(option)
                      }
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.HEIGHT,
                          setTemporaryDraggingValue:
                            heightState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onSliderValueUpdate={(option, variant) => {
                        updateInstantForProperty({
                          propertyType: PropertyType.HEIGHT,
                          option,
                          setTemporaryDraggingValue:
                            heightState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      buildArbitraryClass={(input: string) => {
                        return {
                          tailwind: `h-[${input}]`,
                          css: `height: ${input}`,
                        };
                      }}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.HEIGHT,
                          setTemporaryDraggingValue:
                            heightState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      min={0}
                      step={1}
                      tooltipContent="Height"
                    />
                  </div>
                </div>

                {/* Min/Max Width section */}
                {(showMinWidth ||
                  showMaxWidth ||
                  showMinHeight ||
                  showMaxHeight) && (
                  <div className="grid grid-cols-2 gap-2">
                    {(showMinWidth || showMaxWidth) && (
                      <div className="flex flex-col gap-2">
                        {showMinWidth && (
                          <DraggableInput
                            propertyState={minWidthState}
                            placeholder="Min W"
                            labelContent={
                              <Minimize2
                                width={15}
                                height={15}
                                className="pr-1 rotate-45"
                              />
                            }
                            comboboxOptions={minWidthProperties}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.MIN_WIDTH,
                                setTemporaryDraggingValue:
                                  minWidthState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType: PropertyType.MIN_WIDTH,
                                option,
                                setTemporaryDraggingValue:
                                  minWidthState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `min-w-[${input}]`,
                                css: `min-width: ${input}`,
                              };
                            }}
                            onClear={(variant?: string) => {
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.MIN_WIDTH,
                                setTemporaryDraggingValue:
                                  minWidthState.setTemporaryDraggingValue,
                                variant,
                              });
                              setShowMinWidth(false);
                            }}
                            min={0}
                            step={1}
                            tooltipContent="Minimum width"
                          />
                        )}

                        {showMaxWidth && (
                          <DraggableInput
                            propertyState={maxWidthState}
                            placeholder="Max W"
                            labelContent={
                              <Maximize2
                                className="w-4 h-4 rotate-45"
                                strokeWidth={1.5}
                              />
                            }
                            comboboxOptions={maxWidthProperties}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.MAX_WIDTH,
                                setTemporaryDraggingValue:
                                  maxWidthState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType: PropertyType.MAX_WIDTH,
                                option,
                                setTemporaryDraggingValue:
                                  maxWidthState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onClear={(variant?: string) => {
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.MAX_WIDTH,
                                setTemporaryDraggingValue:
                                  maxWidthState.setTemporaryDraggingValue,
                                variant,
                              });
                              setShowMaxWidth(false);
                            }}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `max-w-[${input}]`,
                                css: `max-width: ${input}`,
                              };
                            }}
                            min={0}
                            step={1}
                            tooltipContent="Maximum width"
                          />
                        )}
                      </div>
                    )}

                    {(showMinHeight || showMaxHeight) && (
                      <div className="flex flex-col gap-2">
                        {showMinHeight && (
                          <DraggableInput
                            propertyState={minHeightState}
                            placeholder="Min H"
                            labelContent={
                              <Minimize2
                                className="rotate-135"
                                width={15}
                                height={15}
                              />
                            }
                            comboboxOptions={minHeightProperties}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.MIN_HEIGHT,
                                setTemporaryDraggingValue:
                                  minHeightState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType: PropertyType.MIN_HEIGHT,
                                option,
                                setTemporaryDraggingValue:
                                  minHeightState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onClear={(variant?: string) => {
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.MIN_HEIGHT,
                                setTemporaryDraggingValue:
                                  minHeightState.setTemporaryDraggingValue,
                                variant,
                              });
                              setShowMinHeight(false);
                            }}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `min-h-[${input}]`,
                                css: `min-height: ${input}`,
                              };
                            }}
                            min={0}
                            step={1}
                            tooltipContent="Minimum height"
                          />
                        )}

                        {/* Min/Max Height Section */}

                        {showMaxHeight && (
                          <DraggableInput
                            propertyState={maxHeightState}
                            placeholder="Max H"
                            labelContent={
                              <Maximize2
                                className="w-4 h-4 rotate-135"
                                strokeWidth={1.5}
                              />
                            }
                            comboboxOptions={maxHeightProperties}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.MAX_HEIGHT,
                                setTemporaryDraggingValue:
                                  maxHeightState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType: PropertyType.MAX_HEIGHT,
                                option,
                                setTemporaryDraggingValue:
                                  maxHeightState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onClear={(variant?: string) => {
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.MAX_HEIGHT,
                                setTemporaryDraggingValue:
                                  maxHeightState.setTemporaryDraggingValue,
                                variant,
                              });
                              setShowMaxHeight(false);
                            }}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `max-h-[${input}]`,
                                css: `max-height: ${input}`,
                              };
                            }}
                            min={0}
                            step={1}
                            tooltipContent="Maximum height"
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          }
        </SectionHeader>
        <hr />

        <div className="flex flex-col">
          <SectionHeader
            title="Spacing"
            isExpanded={showSpacingSection}
            onToggle={() => setShowSpacingSection((prev) => !prev)}
            isActive={showSpacingSection}
          >
            {
              <>
                <div className="text-xs text-secondary-n-400 mb-1">Padding</div>
                <div className="flex items-start justify-between gap-2">
                  {!paddingExpanded && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <DraggableInput
                          propertyState={paddingHorizontalState}
                          labelContent={<SpacingIcon />}
                          buildArbitraryClass={(input: string) => {
                            return {
                              tailwind: `px-[${input}]`,
                              css: `padding-left: ${input}; padding-right: ${input}`,
                            };
                          }}
                          comboboxOptions={paddingHorizontalProperties}
                          onSliderValueUpdate={(option, variant) =>
                            updateInstantForProperty({
                              propertyType: PropertyType.PADDING_HORIZONTAL,
                              option,
                              setTemporaryDraggingValue:
                                paddingHorizontalState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          step={1}
                          min={0}
                          onComboboxSelected={(option, variant) =>
                            pickTailwindClassForProperty({
                              option,
                              propertyType: PropertyType.PADDING_HORIZONTAL,
                              setTemporaryDraggingValue:
                                paddingHorizontalState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          onClear={(variant?: string) =>
                            clearTailwindClassForProperty({
                              propertyType: PropertyType.PADDING_HORIZONTAL,
                              setTemporaryDraggingValue:
                                paddingHorizontalState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          tooltipContent="Horizontal padding"
                        />
                      </div>
                      <div className="flex-1">
                        <DraggableInput
                          propertyState={paddingVerticalState}
                          labelContent={<SpacingIcon className="rotate-90" />}
                          buildArbitraryClass={(input: string) => {
                            return {
                              tailwind: `py-[${input}]`,
                              css: `padding-top: ${input}; padding-bottom: ${input}`,
                            };
                          }}
                          onSliderValueUpdate={(option, variant) =>
                            updateInstantForProperty({
                              propertyType: PropertyType.PADDING_VERTICAL,
                              option,
                              setTemporaryDraggingValue:
                                paddingVerticalState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          step={1}
                          min={0}
                          comboboxOptions={paddingVerticalProperties}
                          onComboboxSelected={(option, variant) =>
                            pickTailwindClassForProperty({
                              option,
                              propertyType: PropertyType.PADDING_VERTICAL,
                              setTemporaryDraggingValue:
                                paddingVerticalState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          onClear={(variant?: string) =>
                            clearTailwindClassForProperty({
                              propertyType: PropertyType.PADDING_VERTICAL,
                              setTemporaryDraggingValue:
                                paddingVerticalState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          tooltipContent="Vertical padding"
                        />
                      </div>
                    </div>
                  )}
                  {paddingExpanded && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1">
                          <DraggableInput
                            labelContent={<AlignLeftIcon />}
                            propertyState={paddingLeftState}
                            comboboxOptions={paddingLeftProperties}
                            onSliderValueUpdate={(option, variant) =>
                              updateInstantForProperty({
                                propertyType: PropertyType.PADDING_LEFT,
                                option,
                                setTemporaryDraggingValue:
                                  paddingLeftState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            onComboboxSelected={(option, variant) =>
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.PADDING_LEFT,
                                setTemporaryDraggingValue:
                                  paddingLeftState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `pl-[${input}]`,
                                css: `padding-left: ${input};`,
                              };
                            }}
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.PADDING_LEFT,
                                setTemporaryDraggingValue:
                                  paddingLeftState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            step={1}
                            min={0}
                            tooltipContent="Left padding"
                          />
                        </div>

                        <div className="flex-1">
                          <DraggableInput
                            labelContent={
                              <AlignLeftIcon className="rotate-180" />
                            }
                            propertyState={paddingRightState}
                            comboboxOptions={paddingRightProperties}
                            onSliderValueUpdate={(option, variant) =>
                              updateInstantForProperty({
                                propertyType: PropertyType.PADDING_RIGHT,
                                option,
                                setTemporaryDraggingValue:
                                  paddingRightState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            onComboboxSelected={(option, variant) =>
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.PADDING_RIGHT,
                                setTemporaryDraggingValue:
                                  paddingRightState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `pr-[${input}]`,
                                css: `padding-right: ${input};`,
                              };
                            }}
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.PADDING_RIGHT,
                                setTemporaryDraggingValue:
                                  paddingRightState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            step={1}
                            min={0}
                            tooltipContent="Right padding"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <DraggableInput
                            labelContent={
                              <AlignLeftIcon className="rotate-90" />
                            }
                            propertyState={paddingTopState}
                            comboboxOptions={paddingTopProperties}
                            onSliderValueUpdate={(option, variant) =>
                              updateInstantForProperty({
                                propertyType: PropertyType.PADDING_TOP,
                                option,
                                setTemporaryDraggingValue:
                                  paddingTopState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            onComboboxSelected={(option, variant) =>
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.PADDING_TOP,
                                setTemporaryDraggingValue:
                                  paddingTopState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `pt-[${input}]`,
                                css: `padding-top: ${input};`,
                              };
                            }}
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.PADDING_TOP,
                                setTemporaryDraggingValue:
                                  paddingTopState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            step={1}
                            min={0}
                            tooltipContent="Top padding"
                          />
                        </div>
                        <div className="flex-1">
                          <DraggableInput
                            labelContent={
                              <AlignLeftIcon className="-rotate-90" />
                            }
                            propertyState={paddingBottomState}
                            comboboxOptions={paddingBottomProperties}
                            onSliderValueUpdate={(option, variant) =>
                              updateInstantForProperty({
                                propertyType: PropertyType.PADDING_BOTTOM,
                                option,
                                setTemporaryDraggingValue:
                                  paddingBottomState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            onComboboxSelected={(option, variant) =>
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.PADDING_BOTTOM,
                                setTemporaryDraggingValue:
                                  paddingBottomState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `pb-[${input}]`,
                                css: `padding-bottom: ${input};`,
                              };
                            }}
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.PADDING_BOTTOM,
                                setTemporaryDraggingValue:
                                  paddingBottomState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            step={1}
                            min={0}
                            tooltipContent="Bottom padding"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col items-end justify-center">
                    <div
                      className={
                        "h-7 w-7 flex items-center justify-center border  border-transparent  cursor-pointer p-1.5 rounded-md " +
                        `${
                          paddingExpanded
                            ? "bg-secondary-n-900 text-secondary-n-50"
                            : "bg-secondary-n-950 text-secondary-n-600"
                        }`
                      }
                      onClick={() =>
                        paddingExpandEnabled &&
                        setPaddingExpanded((prev) => !prev)
                      }
                    >
                      <ExpandSettingPadding className="w-5 h-5" />
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-xs text-secondary-n-400 mb-1">
                    Margin
                  </div>
                </div>
                <div className="gap-x-3 gap-y-2">
                  <div className="flex items-start justify-between gap-2">
                    {!marginExpanded && (
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <DraggableInput
                            propertyState={marginHorizontalState}
                            labelContent={<SpacingIcon />}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `mx-[${input}]`,
                                css: `margin-left: ${input}; margin-right: ${input}`,
                              };
                            }}
                            comboboxOptions={marginHorizontalProperties}
                            onSliderValueUpdate={(option, variant) =>
                              updateInstantForProperty({
                                propertyType: PropertyType.MARGIN_HORIZONTAL,
                                option,
                                setTemporaryDraggingValue:
                                  marginHorizontalState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.MARGIN_HORIZONTAL,
                                setTemporaryDraggingValue:
                                  marginHorizontalState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            step={0.25}
                            min={0}
                            onComboboxSelected={(option, variant) =>
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.MARGIN_HORIZONTAL,
                                setTemporaryDraggingValue:
                                  marginHorizontalState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            tooltipContent="Horizontal margin"
                          />
                        </div>
                        <div className="flex-1">
                          <DraggableInput
                            propertyState={marginVerticalState}
                            labelContent={<SpacingIcon className="rotate-90" />}
                            buildArbitraryClass={(input: string) => {
                              return {
                                tailwind: `my-[${input}]`,
                                css: `margin-top: ${input}; margin-bottom: ${input}`,
                              };
                            }}
                            onSliderValueUpdate={(option, variant) =>
                              updateInstantForProperty({
                                propertyType: PropertyType.MARGIN_VERTICAL,
                                option,
                                setTemporaryDraggingValue:
                                  marginVerticalState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType: PropertyType.MARGIN_VERTICAL,
                                setTemporaryDraggingValue:
                                  marginVerticalState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            step={0.25}
                            min={0}
                            comboboxOptions={marginVerticalProperties}
                            onComboboxSelected={(option, variant) =>
                              pickTailwindClassForProperty({
                                option,
                                propertyType: PropertyType.MARGIN_VERTICAL,
                                setTemporaryDraggingValue:
                                  marginVerticalState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                            tooltipContent="Vertical margin"
                          />
                        </div>
                      </div>
                    )}
                    {marginExpanded && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex-1">
                            <DraggableInput
                              labelContent={<AlignLeftIcon />}
                              propertyState={marginLeftState}
                              comboboxOptions={marginLeftProperties}
                              onSliderValueUpdate={(option, variant) =>
                                updateInstantForProperty({
                                  propertyType: PropertyType.MARGIN_LEFT,
                                  option,
                                  setTemporaryDraggingValue:
                                    marginLeftState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              onComboboxSelected={(option, variant) =>
                                pickTailwindClassForProperty({
                                  option,
                                  propertyType: PropertyType.MARGIN_LEFT,
                                  setTemporaryDraggingValue:
                                    marginLeftState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              buildArbitraryClass={(input) => {
                                return {
                                  tailwind: `ml-[${input}]`,
                                  css: `margin-left: ${input};`,
                                };
                              }}
                              onClear={(variant?: string) =>
                                clearTailwindClassForProperty({
                                  propertyType: PropertyType.MARGIN_LEFT,
                                  setTemporaryDraggingValue:
                                    marginLeftState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              step={0.25}
                              min={0}
                              tooltipContent="Left margin"
                            />
                          </div>
                          <div className="flex-1">
                            <DraggableInput
                              labelContent={
                                <AlignLeftIcon className="rotate-180" />
                              }
                              propertyState={marginRightState}
                              comboboxOptions={marginRightProperties}
                              onSliderValueUpdate={(option, variant) =>
                                updateInstantForProperty({
                                  propertyType: PropertyType.MARGIN_RIGHT,
                                  option,
                                  setTemporaryDraggingValue:
                                    marginRightState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              onComboboxSelected={(option, variant) =>
                                pickTailwindClassForProperty({
                                  option,
                                  propertyType: PropertyType.MARGIN_RIGHT,
                                  setTemporaryDraggingValue:
                                    marginRightState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              buildArbitraryClass={(input) => {
                                return {
                                  tailwind: `mr-[${input}]`,
                                  css: `margin-right: ${input};`,
                                };
                              }}
                              step={0.25}
                              min={0}
                              tooltipContent="Right margin"
                              onClear={(variant?: string) => {
                                clearTailwindClassForProperty({
                                  propertyType: PropertyType.MARGIN_RIGHT,
                                  setTemporaryDraggingValue:
                                    marginRightState.setTemporaryDraggingValue,
                                  variant,
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <DraggableInput
                              labelContent={
                                <AlignLeftIcon className="rotate-90" />
                              }
                              propertyState={marginTopState}
                              comboboxOptions={marginTopProperties}
                              onSliderValueUpdate={(option, variant) =>
                                updateInstantForProperty({
                                  propertyType: PropertyType.MARGIN_TOP,
                                  option,
                                  setTemporaryDraggingValue:
                                    marginTopState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              onComboboxSelected={(option, variant) =>
                                pickTailwindClassForProperty({
                                  option,
                                  propertyType: PropertyType.MARGIN_TOP,
                                  setTemporaryDraggingValue:
                                    marginTopState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              buildArbitraryClass={(input) => {
                                return {
                                  tailwind: `mt-[${input}]`,
                                  css: `margin-top: ${input};`,
                                };
                              }}
                              onClear={(variant?: string) =>
                                clearTailwindClassForProperty({
                                  propertyType: PropertyType.MARGIN_TOP,
                                  setTemporaryDraggingValue:
                                    marginTopState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              step={0.25}
                              min={0}
                              tooltipContent="Top margin"
                            />
                          </div>
                          <div className="flex-1">
                            <DraggableInput
                              labelContent={
                                <AlignLeftIcon className="-rotate-90" />
                              }
                              propertyState={marginBottomState}
                              comboboxOptions={marginBottomProperties}
                              onSliderValueUpdate={(option, variant) =>
                                updateInstantForProperty({
                                  propertyType: PropertyType.MARGIN_BOTTOM,
                                  option,
                                  setTemporaryDraggingValue:
                                    marginBottomState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              onComboboxSelected={(option, variant) =>
                                pickTailwindClassForProperty({
                                  option,
                                  propertyType: PropertyType.MARGIN_BOTTOM,
                                  setTemporaryDraggingValue:
                                    marginBottomState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              buildArbitraryClass={(input) => {
                                return {
                                  tailwind: `mb-[${input}]`,
                                  css: `margin-bottom: ${input};`,
                                };
                              }}
                              onClear={(variant?: string) =>
                                clearTailwindClassForProperty({
                                  propertyType: PropertyType.MARGIN_BOTTOM,
                                  setTemporaryDraggingValue:
                                    marginBottomState.setTemporaryDraggingValue,
                                  variant,
                                })
                              }
                              step={0.25}
                              min={0}
                              tooltipContent="Bottom margin"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col items-end justify-center">
                      <div
                        className={`h-7 w-7 flex items-center justify-center border  border-transparent  cursor-pointer p-1.5 rounded-md ${
                          marginExpanded
                            ? "bg-secondary-n-900 text-secondary-n-50"
                            : "bg-secondary-n-950 text-secondary-n-600"
                        }`}
                        onClick={() =>
                          marginExpandEnabled &&
                          setMarginExpanded((prev) => !prev)
                        }
                      >
                        <ExpandSettingPadding className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            }
          </SectionHeader>
        </div>
        <hr />
        <SectionHeader
          title="Layout"
          isExpanded={layoutExpanded}
          onToggle={() => {
            setLayoutExpanded(!layoutExpanded);
            // Also toggle flex if the section is being expanded and flex is not already set
            if (!layoutExpanded && !isFlex) {
              pickTailwindClassForProperty({
                option: {
                  tailwind: "flex",
                  css: "display: flex;",
                },
                propertyType: PropertyType.DISPLAY,
                setTemporaryDraggingValue:
                  displayState.setTemporaryDraggingValue,
              });
            }
          }}
          isActive={layoutExpanded}
        >
          {
            <div className="flex flex-col justify-center gap-x-2 gap-y-2">
              <div className="grid grid-cols-2 gap-2">
                <AlignField
                  flexDirectionState={flexDirectionState}
                  justifyContentState={justifyContentState}
                  alignItemsState={alignItemsState}
                  elementClasses={elementClasses}
                  tailwindClassesMap={tailwindClassesMap}
                  pickTailwindClassForProperty={pickTailwindClassForProperty}
                  clearTailwindClassForProperty={clearTailwindClassForProperty}
                />

                <FlexDirectionWrapField
                  flexWrapState={flexWrapState}
                  flexDirectionState={flexDirectionState}
                  justifyContentState={justifyContentState}
                  alignItemsState={alignItemsState}
                  pickTailwindClassForProperty={pickTailwindClassForProperty}
                  clearTailwindClassForProperty={clearTailwindClassForProperty}
                />
              </div>

              {flexWrapState.propertyValue !== "nowrap" && (
                <AlignChildrenField
                  alignContentState={alignContentState}
                  flexDirectionState={flexDirectionState}
                  justifyContentState={justifyContentState}
                  alignItemsState={alignItemsState}
                  pickTailwindClassForProperty={pickTailwindClassForProperty}
                  clearTailwindClassForProperty={clearTailwindClassForProperty}
                />
              )}

              <WrapContentField
                flexDirectionState={flexDirectionState}
                justifyContentState={justifyContentState}
                alignItemsState={alignItemsState}
                pickTailwindClassForProperty={pickTailwindClassForProperty}
                clearTailwindClassForProperty={clearTailwindClassForProperty}
              />

              <GapField
                columnGapState={columnGapState}
                rowGapState={rowGapState}
                flexDirectionState={flexDirectionState}
                pickTailwindClassForProperty={pickTailwindClassForProperty}
                clearTailwindClassForProperty={clearTailwindClassForProperty}
                updateInstantForProperty={updateInstantForProperty}
              />
            </div>
          }
        </SectionHeader>
        <hr />

        <div className="flex flex-col">
          <SectionHeader
            title="Styles"
            isExpanded={showStyleSection}
            onToggle={() => setShowStyleSection((prev) => !prev)}
            isActive={showStyleSection}
            data-testid="styling-panel-section-styles"
          >
            {
              <div className=" items-center gap-x-2 gap-y-2 relative">
                <div ref={fillModalRef}>
                  <ColorInput
                    propertyType={PropertyType.BACKGROUND_COLOR}
                    propertyState={backgroundColorState}
                    comboboxOptions={sortTailwindColorOptions(
                      backgroundColorProperties,
                    )}
                    setValue={(option) => {
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.BACKGROUND_COLOR,
                        setTemporaryDraggingValue:
                          backgroundColorState.setTemporaryDraggingValue,
                      });
                    }}
                    instantUpdateValue={(option) => {
                      updateInstantForProperty({
                        propertyType: PropertyType.BACKGROUND_COLOR,
                        option,
                        setTemporaryDraggingValue:
                          backgroundColorState.setTemporaryDraggingValue,
                      });
                    }}
                    buildArbitraryClass={(input: string) => ({
                      tailwind: `bg-[${input}]`,
                      css: `background-color: ${input}`,
                      propertyType: PropertyType.BACKGROUND_COLOR,
                    })}
                    onClear={() => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.BACKGROUND_COLOR,
                        setTemporaryDraggingValue:
                          backgroundColorState.setTemporaryDraggingValue,
                      });
                    }}
                    elementClasses={elementClasses}
                    selectedCodebaseId={selectedCodebaseId}
                    pickTailwindClassForProperty={pickTailwindClassForProperty}
                    clearTailwindClassForProperty={
                      clearTailwindClassForProperty
                    }
                    updateInstantForProperty={updateInstantForProperty}
                    sortTailwindColorOptions={sortTailwindColorOptions}
                    setModal={setModal}
                    modalRef={fillModalRef}
                    modalType={StylingModalType.Fill}
                    enableVariants={true}
                    className="h-7"
                    placeholder="Add fill"
                    cssValues={cssValues}
                    data-testid="styling-panel-fill-color-input"
                  />
                  <VariantDot
                    valueByVariant={backgroundColorState.valueByVariant}
                  />
                </div>

                <div className="text-xs text-secondary-n-400 mb-1 mt-2">
                  Rotate
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DraggableInput
                      placeholder="0°"
                      propertyState={rotationState}
                      labelContent={<AngleIcon />}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.ROTATE,
                          setTemporaryDraggingValue:
                            rotationState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onSliderValueUpdate={(option, variant) => {
                        updateInstantForProperty({
                          propertyType: PropertyType.ROTATE,
                          option,
                          setTemporaryDraggingValue:
                            rotationState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      buildArbitraryClass={(input: string) => {
                        return {
                          tailwind: `rotate-[${input}]`,
                          css: `transform: rotate(${input})`,
                        };
                      }}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.ROTATE,
                          setTemporaryDraggingValue:
                            rotationState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      comboboxOptions={rotationProperties}
                      tooltipContent="Rotation"
                    />
                  </div>

                  <div className="flex gap-1">
                    <div
                      className={cn(
                        "h-7 flex items-center justify-center gap-1 bg-secondary-n-950 p-1 rounded-md cursor-pointer",
                      )}
                    >
                      <Button
                        className={cn(
                          "w-7 h-7 p-0 shadow-none flex items-center justify-center rounded-md hover:bg-secondary-n-900 text-secondary-n-700 hover:text-secondary-n-50",
                        )}
                        onClick={() => {
                          resetRotationAndFlips();
                        }}
                        title="Reset Rotation"
                      >
                        <RevertIcon />
                      </Button>
                      <Button
                        className={cn(
                          "w-7 h-7 p-0 shadow-none flex items-center justify-center rounded-md hover:bg-secondary-n-900 text-secondary-n-700 hover:text-secondary-n-50",
                          elementClasses.includes("scale-x-[-1]") &&
                            "bg-secondary-n-900 text-secondary-n-50",
                        )}
                        onClick={() => {
                          toggleArbitraryClass(
                            "scale-x-[-1]",
                            "transform: scaleX(-1);",
                          );
                        }}
                        title="Flip Horizontally"
                      >
                        <FliPIcon className="rotate-90" />
                      </Button>
                      <Button
                        className={cn(
                          "w-7 h-7 p-0 shadow-none flex items-center justify-center rounded-md hover:bg-secondary-n-900 text-secondary-n-700 hover:text-secondary-n-50",
                          elementClasses.includes("scale-y-[-1]") &&
                            "bg-secondary-n-900 text-secondary-n-50",
                        )}
                        onClick={() => {
                          toggleArbitraryClass(
                            "scale-y-[-1]",
                            "transform: scaleY(-1);",
                          );
                        }}
                        title="Flip Vertically"
                      >
                        <FliPIcon />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-secondary-n-400 mt-2 mb-1">
                  Border
                </div>

                <div className="col-span-8" ref={borderModalRef}>
                  <ColorInput
                    propertyType={PropertyType.BORDER_COLOR}
                    propertyState={borderColorState}
                    comboboxOptions={sortTailwindColorOptions(
                      borderColorProperties,
                    )}
                    setValue={(option) => {
                      if (
                        !borderWidthState.isSet &&
                        borderWidthState.cssValue === "0px"
                      ) {
                        pickTailwindClassForProperty({
                          option: {
                            tailwind: "border",
                            css: "border-width: 1px;",
                          },
                          propertyType: PropertyType.BORDER_WIDTH,
                          setTemporaryDraggingValue:
                            borderWidthState.setTemporaryDraggingValue,
                        });
                      }

                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.BORDER_COLOR,
                        setTemporaryDraggingValue:
                          borderColorState.setTemporaryDraggingValue,
                      });
                    }}
                    instantUpdateValue={(option) => {
                      if (
                        !borderWidthState.isSet &&
                        borderWidthState.cssValue === "0px"
                      ) {
                        updateInstantForProperty({
                          propertyType: PropertyType.BORDER_WIDTH,
                          option: {
                            tailwind: "border",
                            css: "border-width: 1px;",
                          },
                          setTemporaryDraggingValue:
                            borderColorState.setTemporaryDraggingValue,
                        });
                      }

                      updateInstantForProperty({
                        propertyType: PropertyType.BORDER_COLOR,
                        option,
                        setTemporaryDraggingValue:
                          borderColorState.setTemporaryDraggingValue,
                      });
                    }}
                    buildArbitraryClass={(input: string) => ({
                      tailwind: `border-[${input}]`,
                      css: `border-color: ${input};`,
                    })}
                    pickTailwindClassForProperty={({
                      option,
                      propertyType,
                      setTemporaryDraggingValue,
                      variant,
                    }) => {
                      if (
                        !borderWidthState.isSet &&
                        borderWidthState.cssValue === "0px"
                      ) {
                        pickTailwindClassForProperty({
                          option: {
                            tailwind: "border",
                            css: "border-width: 1px;",
                          },
                          propertyType: PropertyType.BORDER_WIDTH,
                          setTemporaryDraggingValue:
                            borderWidthState.setTemporaryDraggingValue,
                        });
                      }

                      pickTailwindClassForProperty({
                        option,
                        propertyType,
                        setTemporaryDraggingValue:
                          setTemporaryDraggingValue ||
                          borderColorState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    clearTailwindClassForProperty={
                      clearTailwindClassForProperty
                    }
                    updateInstantForProperty={updateInstantForProperty}
                    elementClasses={elementClasses}
                    selectedCodebaseId={selectedCodebaseId}
                    cssValues={cssValues}
                    onClear={() =>
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.BORDER_COLOR,
                        setTemporaryDraggingValue:
                          borderColorState.setTemporaryDraggingValue,
                      })
                    }
                    placeholder="Add border color"
                    setModal={setModal}
                    modalRef={borderModalRef}
                    modalType={StylingModalType.Border}
                    sortTailwindColorOptions={sortTailwindColorOptions}
                    enableVariants={true}
                    onClearVariant={() => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.BORDER_COLOR,
                        setTemporaryDraggingValue:
                          borderColorState.setTemporaryDraggingValue,
                      });
                    }}
                  />

                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1">
                      <div className="col-span-2">
                        <DraggableInput
                          placeholder="0px"
                          onSliderValueUpdate={(option, variant) =>
                            updateInstantForProperty({
                              propertyType: PropertyType.BORDER_WIDTH,
                              option,
                              setTemporaryDraggingValue:
                                borderWidthState.setTemporaryDraggingValue,
                              variant,
                            })
                          }
                          labelContent={<SpacingIcon />}
                          min={0}
                          step={0.25}
                          propertyState={borderWidthState}
                          buildArbitraryClass={(input) => ({
                            tailwind: `border-[${input}]`,
                            css: `border-width: ${input};`,
                          })}
                          comboboxOptions={borderWidthProperties}
                          tooltipContent="Border width"
                          onComboboxSelected={(option, variant) => {
                            pickTailwindClassForProperty({
                              option,
                              propertyType: PropertyType.BORDER_WIDTH,
                              setTemporaryDraggingValue:
                                borderWidthState.setTemporaryDraggingValue,
                              variant,
                            });
                          }}
                          disabled={borderWidthExpanded}
                          onClear={(variant?: string) => {
                            clearTailwindClassForProperty({
                              propertyType: PropertyType.BORDER_WIDTH,
                              setTemporaryDraggingValue:
                                borderWidthState.setTemporaryDraggingValue,
                              variant,
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <DraggableInput
                        placeholder="Solid"
                        labelContent={<SolidBarIcon />}
                        draggable={false}
                        onSliderValueUpdate={(option, variant) =>
                          updateInstantForProperty({
                            propertyType: PropertyType.BORDER_STYLE,
                            option,
                            setTemporaryDraggingValue:
                              borderStyleState.setTemporaryDraggingValue,
                            variant,
                          })
                        }
                        propertyState={borderStyleState}
                        comboboxOptions={borderStyleProperties}
                        tooltipContent="Border style"
                        onComboboxSelected={(option, variant) => {
                          pickTailwindClassForProperty({
                            option,
                            propertyType: PropertyType.BORDER_STYLE,
                            setTemporaryDraggingValue:
                              borderStyleState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                        onClear={(variant?: string) => {
                          clearTailwindClassForProperty({
                            propertyType: PropertyType.BORDER_STYLE,
                            setTemporaryDraggingValue:
                              borderStyleState.setTemporaryDraggingValue,
                            variant,
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-row items-center gap-x-2 mt-2">
                  <div className="flex-1">
                    <DraggableInput
                      propertyState={borderRadiusState}
                      labelContent={<Scan className="w-4 h-4" />}
                      disabled={radiusExpanded}
                      min={0}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BORDER_RADIUS,
                          setTemporaryDraggingValue:
                            borderRadiusState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onSliderValueUpdate={(option, variant) => {
                        updateInstantForProperty({
                          propertyType: PropertyType.BORDER_RADIUS,
                          option,
                          setTemporaryDraggingValue:
                            borderRadiusState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      comboboxOptions={borderRadiusProperties}
                      buildArbitraryClass={(input) => {
                        return {
                          tailwind: `rounded-[${input}]`,
                          css: `border-top-left-radius: ${input}; border-top-right-radius: ${input}; border-bottom-left-radius: ${input}; border-bottom-right-radius: ${input}`,
                        };
                      }}
                      tooltipContent="Border radius"
                      onClear={(variant?: string) =>
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BORDER_RADIUS,
                          setTemporaryDraggingValue:
                            borderRadiusState.setTemporaryDraggingValue,
                          variant,
                        })
                      }
                      data-testid="styling-panel-border-radius-input"
                    />
                  </div>

                  <div
                    className={cn(
                      "h-7 flex items-center justify-center gap-1 bg-secondary-n-950 p-1 rounded-md cursor-pointer",
                      radiusExpanded
                        ? "bg-secondary-n-900 text-secondary-n-50"
                        : "bg-secondary-n-950 text-secondary-n-600",
                    )}
                    onClick={() => setRadiusExpanded((prev) => !prev)}
                  >
                    <Scan className="w-5 h-5 p-[3px]" />
                  </div>
                </div>

                {/* Individual Radius */}
                {radiusExpanded && (
                  <div className="mt-2">
                    <div className="col-span-8">
                      <div className="grid grid-cols-2 gap-2 gap-x-2 border border-transparent">
                        <div>
                          <DraggableInput
                            labelContent={<CornerTopLeftIcon />}
                            propertyState={topLeftRadiusState}
                            onInputFocus={() => setActiveRadius(0)}
                            comboboxOptions={topLeftRadiusProperties}
                            min={0}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType:
                                  PropertyType.BORDER_TOP_LEFT_RADIUS,
                                setTemporaryDraggingValue:
                                  topLeftRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType:
                                  PropertyType.BORDER_TOP_LEFT_RADIUS,
                                option,
                                setTemporaryDraggingValue:
                                  topLeftRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `rounded-tl-[${input}]`,
                                css: `border-top-left-radius: ${input};`,
                              };
                            }}
                            tooltipContent="Top left radius"
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType:
                                  PropertyType.BORDER_TOP_LEFT_RADIUS,
                                setTemporaryDraggingValue:
                                  topLeftRadiusState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                          />
                        </div>
                        <div>
                          <DraggableInput
                            labelContent={<CornerTopRightIcon />}
                            propertyState={topRightRadiusState}
                            onInputFocus={() => setActiveRadius(1)}
                            comboboxOptions={topRightRadiusProperties}
                            min={0}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType:
                                  PropertyType.BORDER_TOP_RIGHT_RADIUS,
                                setTemporaryDraggingValue:
                                  topRightRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType:
                                  PropertyType.BORDER_TOP_RIGHT_RADIUS,
                                option,
                                setTemporaryDraggingValue:
                                  topRightRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `rounded-tr-[${input}]`,
                                css: `border-top-right-radius: ${input};`,
                              };
                            }}
                            tooltipContent="Top right radius"
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType:
                                  PropertyType.BORDER_TOP_RIGHT_RADIUS,
                                setTemporaryDraggingValue:
                                  topRightRadiusState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                          />
                        </div>
                        <div>
                          <DraggableInput
                            labelContent={<CornerBottomLeftIcon />}
                            propertyState={bottomLeftRadiusState}
                            onInputFocus={() => setActiveRadius(3)}
                            comboboxOptions={bottomLeftRadiusProperties}
                            min={0}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType:
                                  PropertyType.BORDER_BOTTOM_LEFT_RADIUS,
                                setTemporaryDraggingValue:
                                  bottomLeftRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType:
                                  PropertyType.BORDER_BOTTOM_LEFT_RADIUS,
                                option,
                                setTemporaryDraggingValue:
                                  bottomLeftRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `rounded-bl-[${input}]`,
                                css: `border-bottom-left-radius: ${input};`,
                              };
                            }}
                            tooltipContent="Bottom left radius"
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType:
                                  PropertyType.BORDER_BOTTOM_LEFT_RADIUS,
                                setTemporaryDraggingValue:
                                  bottomLeftRadiusState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                          />
                        </div>
                        <div>
                          <DraggableInput
                            labelContent={<CornerBottomRightIcon />}
                            propertyState={bottomRightRadiusState}
                            onInputFocus={() => setActiveRadius(2)}
                            comboboxOptions={bottomRightRadiusProperties}
                            min={0}
                            onComboboxSelected={(option, variant) => {
                              pickTailwindClassForProperty({
                                option,
                                propertyType:
                                  PropertyType.BORDER_BOTTOM_RIGHT_RADIUS,
                                setTemporaryDraggingValue:
                                  bottomRightRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            onSliderValueUpdate={(option, variant) => {
                              updateInstantForProperty({
                                propertyType:
                                  PropertyType.BORDER_BOTTOM_RIGHT_RADIUS,
                                option,
                                setTemporaryDraggingValue:
                                  bottomRightRadiusState.setTemporaryDraggingValue,
                                variant,
                              });
                            }}
                            buildArbitraryClass={(input) => {
                              return {
                                tailwind: `rounded-br-[${input}]`,
                                css: `border-bottom-right-radius: ${input};`,
                              };
                            }}
                            tooltipContent="Bottom right radius"
                            onClear={(variant?: string) =>
                              clearTailwindClassForProperty({
                                propertyType:
                                  PropertyType.BORDER_BOTTOM_RIGHT_RADIUS,
                                setTemporaryDraggingValue:
                                  bottomRightRadiusState.setTemporaryDraggingValue,
                                variant,
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            }
          </SectionHeader>
        </div>
        <hr />
        <div className="flex flex-col">
          <SectionHeader
            title="Typography"
            isExpanded={showTextSection}
            onToggle={() => setShowTextSection((prev) => !prev)}
            isActive={showTextSection}
          >
            {
              <div className="items-center gap-x-2 gap-y-2">
                <DraggableInput
                  draggable={false}
                  propertyState={fontFamilyState}
                  labelContent={<Type className="w-4 h-4" />}
                  buildArbitraryClass={(input: string) => {
                    return {
                      tailwind: `font-[${input}]`,
                      css: `font-family: ${input};`,
                    };
                  }}
                  comboboxOptions={fontFamilyProperties}
                  onComboboxSelected={(option, variant) =>
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.FONT_FAMILY,
                      setTemporaryDraggingValue:
                        fontFamilyState.setTemporaryDraggingValue,
                      variant,
                    })
                  }
                  tooltipContent="Font family"
                  onClear={(variant?: string) => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.FONT_FAMILY,
                      setTemporaryDraggingValue:
                        fontFamilyState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                />

                <div className="grid grid-cols-2 gap-2 mt-2">
                  <DraggableInput
                    propertyState={fontWeightState}
                    labelContent={
                      <Type strokeWidth={3.5} className="w-4 h-4" />
                    }
                    min={100}
                    max={900}
                    step={100}
                    tooltipContent="Font weight"
                    fixedOptionsOnly
                    onSliderValueUpdate={(option, variant) =>
                      updateInstantForProperty({
                        propertyType: PropertyType.FONT_WEIGHT,
                        option,
                        setTemporaryDraggingValue:
                          fontWeightState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    buildOptionPreview={buildFontWeightColorPreview}
                    comboboxOptions={fontWeightProperties}
                    onComboboxSelected={(option, variant) =>
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.FONT_WEIGHT,
                        setTemporaryDraggingValue:
                          fontWeightState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.FONT_WEIGHT,
                        setTemporaryDraggingValue:
                          fontWeightState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                  />

                  <DraggableInput
                    propertyState={fontSizeState}
                    labelContent={
                      <FontSizeIcon className="transform -scale-x-100" />
                    }
                    min={0}
                    step={0.25}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `text-[${input}]`,
                        css: `font-size: ${input};`,
                      };
                    }}
                    onSliderValueUpdate={(option, variant) =>
                      updateInstantForProperty({
                        propertyType: PropertyType.FONT_SIZE,
                        option,
                        setTemporaryDraggingValue:
                          fontSizeState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    comboboxOptions={fontSizeProperties}
                    onComboboxSelected={(option, variant) =>
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.FONT_SIZE,
                        setTemporaryDraggingValue:
                          fontSizeState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    tooltipContent="Font size"
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.FONT_SIZE,
                        setTemporaryDraggingValue:
                          fontSizeState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                  />

                  <DraggableInput
                    propertyState={letterSpacingState}
                    labelContent={<LetterSpacingIcon />}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `tracking-[${input}]`,
                        css: `letter-spacing: ${input};`,
                      };
                    }}
                    comboboxOptions={letterSpacingProperties}
                    onSliderValueUpdate={(option, variant) =>
                      updateInstantForProperty({
                        propertyType: PropertyType.LETTER_SPACING,
                        option,
                        setTemporaryDraggingValue:
                          letterSpacingState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    onComboboxSelected={(option, variant) =>
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.LETTER_SPACING,
                        setTemporaryDraggingValue:
                          letterSpacingState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    step={0.05}
                    tooltipContent="Letter spacing"
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.LETTER_SPACING,
                        setTemporaryDraggingValue:
                          letterSpacingState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                  />

                  <DraggableInput
                    propertyState={lineHeightState}
                    labelContent={<LineHeightIcon />}
                    min={0}
                    step={0.25}
                    buildArbitraryClass={(input: string) => {
                      return {
                        tailwind: `leading-[${input}]`,
                        css: `line-height: ${input};`,
                      };
                    }}
                    onSliderValueUpdate={(option, variant) =>
                      updateInstantForProperty({
                        propertyType: PropertyType.LINE_HEIGHT,
                        option,
                        setTemporaryDraggingValue:
                          lineHeightState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    comboboxOptions={lineHeightProperties}
                    onComboboxSelected={(option, variant) =>
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.LINE_HEIGHT,
                        setTemporaryDraggingValue:
                          lineHeightState.setTemporaryDraggingValue,
                        variant,
                      })
                    }
                    tooltipContent="Line height"
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.LINE_HEIGHT,
                        setTemporaryDraggingValue:
                          lineHeightState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                  />

                  <div ref={textColorModalRef}>
                    <ColorInput
                      propertyType={PropertyType.TEXT_COLOR}
                      propertyState={textColorState}
                      comboboxOptions={sortTailwindColorOptions(
                        textColorProperties,
                      )}
                      setValue={(option) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.TEXT_COLOR,
                          setTemporaryDraggingValue:
                            textColorState.setTemporaryDraggingValue,
                        });
                      }}
                      instantUpdateValue={(option) =>
                        updateInstantForProperty({
                          propertyType: PropertyType.TEXT_COLOR,
                          option,
                          setTemporaryDraggingValue:
                            textColorState.setTemporaryDraggingValue,
                        })
                      }
                      buildArbitraryClass={(input: string) => ({
                        tailwind: `text-[${input}]`,
                        css: `color: ${input};`,
                      })}
                      pickTailwindClassForProperty={
                        pickTailwindClassForProperty
                      }
                      clearTailwindClassForProperty={
                        clearTailwindClassForProperty
                      }
                      updateInstantForProperty={updateInstantForProperty}
                      elementClasses={elementClasses}
                      selectedCodebaseId={selectedCodebaseId}
                      cssValues={cssValues}
                      onClear={() =>
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.TEXT_COLOR,
                          setTemporaryDraggingValue:
                            textColorState.setTemporaryDraggingValue,
                        })
                      }
                      placeholder="Text color"
                      setModal={setModal}
                      modalRef={textColorModalRef}
                      modalType={StylingModalType.TextColor}
                      sortTailwindColorOptions={sortTailwindColorOptions}
                      enableVariants={true}
                      onClearVariant={() => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.TEXT_COLOR,
                          setTemporaryDraggingValue:
                            textColorState.setTemporaryDraggingValue,
                        });
                      }}
                    />
                  </div>

                  <TextAlignField
                    textAlignState={textAlignState}
                    pickTailwindClassForProperty={pickTailwindClassForProperty}
                    clearTailwindClassForProperty={
                      clearTailwindClassForProperty
                    }
                  />

                  <TextDecorationField
                    textDecorationLineState={textDecorationLineState}
                    pickTailwindClassForProperty={pickTailwindClassForProperty}
                    clearTailwindClassForProperty={
                      clearTailwindClassForProperty
                    }
                  />
                </div>
              </div>
            }
          </SectionHeader>
        </div>
        <hr />
        <div className="flex flex-col w-full space-y-2">
          <SectionHeader
            title="Effects"
            isExpanded={showEffectSection}
            onToggle={() => setShowEffectSection((prev) => !prev)}
            isActive={showEffectSection}
            data-testid="styling-panel-section-effects"
          >
            {
              <div className="grid grid-cols-7 items-center pl-2 pr-2 gap-x-2 gap-y-2">
                <div className="col-span-3 text-xs text-secondary-800 font-medium">
                  Animation
                </div>
                <div className="col-span-4 flex justify-between flex-row">
                  <div className="flex flex-row grow">
                    <DraggableInput
                      propertyState={animationState}
                      labelContent={<MagicWandIcon />}
                      comboboxOptions={animationProperties}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.ANIMATION,
                          setTemporaryDraggingValue:
                            animationState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      draggable={false}
                      tooltipContent="Animation"
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.ANIMATION,
                          setTemporaryDraggingValue:
                            animationState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                    />
                  </div>
                </div>
                <div className="col-span-3 text-xs text-secondary-800 font-medium">
                  Shadow
                </div>
                <div className="col-span-4 flex justify-between flex-row">
                  <div className="flex flex-row grow">
                    <DraggableInput
                      propertyState={boxShadowState}
                      labelContent={<ShadowIcon />}
                      comboboxOptions={boxShadowProperties}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BOX_SHADOW,
                          setTemporaryDraggingValue:
                            boxShadowState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BOX_SHADOW,
                          setTemporaryDraggingValue:
                            boxShadowState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      draggable={false}
                      tooltipContent="Shadow"
                    />
                  </div>
                </div>
                <div className="col-span-3 text-xs text-secondary-800 font-medium">
                  Blur
                </div>
                <div className="col-span-4 flex justify-between flex-row">
                  <div className="flex flex-row grow">
                    <DraggableInput
                      propertyState={blurState}
                      labelContent={<BlendingModeIcon />}
                      min={0}
                      step={0.25}
                      comboboxOptions={blurProperties}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BLUR,
                          setTemporaryDraggingValue:
                            blurState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BLUR,
                          setTemporaryDraggingValue:
                            blurState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      buildArbitraryClass={(input: string) => {
                        return {
                          tailwind: `blur-[${input}]`,
                          css: `--tw-blur: blur(${input}); filter: var(--tw-blur);`,
                        };
                      }}
                      onSliderValueUpdate={(option, variant) =>
                        updateInstantForProperty({
                          propertyType: PropertyType.BLUR,
                          option,
                          setTemporaryDraggingValue:
                            blurState.setTemporaryDraggingValue,
                          variant,
                        })
                      }
                      draggable={true}
                      tooltipContent="Blur"
                      data-testid="styling-panel-blur-input"
                    />
                  </div>
                </div>
              </div>
            }
          </SectionHeader>
        </div>
        {/* Extra space on the bottom */}
        <div className="h-6" />

        {/* <DebugInformation
          cssValues={cssValues}
          selectedElement={selectedElement}
          selectedTreeElement={selectedTreeElement}
          elementClassList={elementClassList || []}
          parentCssEvals={parentCssEvals || {}}
        /> */}
      </ComboboxContainer>
      <ModalContainer
        className={cn("w-[250px]! border-none! gap-y-3!")}
        title={modalTypeToTitle[modal.type]}
        visible={modal.type !== StylingModalType.None}
        onDismiss={() => setModal({ type: StylingModalType.None })}
        anchorRef={modal.anchor}
        containerRef={containerRef}
      >
        <ComboboxContainer>
          {modal.type === StylingModalType.TextColor && (
            <div className="grid grid-cols-1">
              <div style={{ position: "relative" }}>
                <CustomColorPicker
                  propertyState={textColorState}
                  buildArbitraryClass={(input: string) => {
                    return {
                      tailwind: `text-[${input}]`,
                      css: `color: ${input};`,
                    };
                  }}
                  instantUpdateValue={(option) =>
                    updateInstantForProperty({
                      propertyType: PropertyType.TEXT_COLOR,
                      option,
                      setTemporaryDraggingValue:
                        textColorState.setTemporaryDraggingValue,
                    })
                  }
                  comboboxOptions={sortTailwindColorOptions(
                    textColorProperties,
                  )}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.TEXT_COLOR,
                      setTemporaryDraggingValue:
                        textColorState.setTemporaryDraggingValue,
                    });
                  }}
                  buildOptionPreview={buildTextColorPreview}
                  tooltipContent="Text color"
                  onClear={(variant?: string) => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.TEXT_COLOR,
                      setTemporaryDraggingValue:
                        textColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                  onOpenVariantModal={() => setTextColorVariantModalOpen(true)}
                />
                {/* Hidden button for ChevronDown anchor */}
                <button
                  type="button"
                  ref={textColorVariantAnchorRef}
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    width: 1,
                    height: 1,
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                  tabIndex={-1}
                  aria-hidden
                />
              </div>
            </div>
          )}
          {modal.type === StylingModalType.Border && (
            <div className="grid grid-cols-8 items-center gap-x-1 gap-y-2">
              <div className="col-span-8">
                <div style={{ position: "relative" }}>
                  <CustomColorPicker
                    propertyState={borderColorState}
                    buildArbitraryClass={(input) => {
                      const baseOption = {
                        tailwind: `border-[${input}]`,
                        css: `border-color: ${input};`,
                      };
                      return convertOptionCssVariableToTailwindClass(
                        baseOption,
                      );
                    }}
                    instantUpdateValue={(option) => {
                      if (
                        !borderWidthState.isSet &&
                        borderWidthState.cssValue === "0px"
                      ) {
                        updateInstantForProperty({
                          propertyType: PropertyType.BORDER_WIDTH,
                          option: {
                            tailwind: "border",
                            css: "border-width: 1px;",
                          },
                          setTemporaryDraggingValue:
                            borderColorState.setTemporaryDraggingValue,
                        });
                      }
                      updateInstantForProperty({
                        propertyType: PropertyType.BORDER_COLOR,
                        option,
                        setTemporaryDraggingValue:
                          borderColorState.setTemporaryDraggingValue,
                      });
                    }}
                    comboboxOptions={sortTailwindColorOptions(
                      borderColorProperties,
                    )}
                    setValue={(option) => {
                      if (
                        !borderWidthState.isSet &&
                        borderWidthState.cssValue === "0px"
                      ) {
                        pickTailwindClassForProperty({
                          option: {
                            tailwind: "border",
                            css: "border-width: 1px;",
                          },
                          propertyType: PropertyType.BORDER_WIDTH,
                          setTemporaryDraggingValue:
                            borderWidthState.setTemporaryDraggingValue,
                        });
                      }
                      pickTailwindClassForProperty({
                        option,
                        propertyType: PropertyType.BORDER_COLOR,
                        setTemporaryDraggingValue:
                          borderColorState.setTemporaryDraggingValue,
                      });
                    }}
                    tooltipContent="Border color"
                    onClear={(variant?: string) => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.BORDER_COLOR,
                        setTemporaryDraggingValue:
                          borderColorState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    showHex={true}
                    onOpenVariantModal={() =>
                      setBorderColorVariantModalOpen(true)
                    }
                  />
                  {/* Hidden button for ChevronDown anchor */}
                  <button
                    type="button"
                    ref={borderColorVariantAnchorRef}
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      width: 1,
                      height: 1,
                      opacity: 0,
                      pointerEvents: "none",
                    }}
                    tabIndex={-1}
                    aria-hidden
                  />
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-xs text-secondary-800 font-medium">
                  Style
                </div>
              </div>
              <div className="col-span-6">
                <DraggableInput
                  labelContent={<BorderStyleIcon />}
                  draggable={false}
                  onSliderValueUpdate={(option, variant) =>
                    updateInstantForProperty({
                      propertyType: PropertyType.BORDER_STYLE,
                      option,
                      setTemporaryDraggingValue:
                        borderStyleState.setTemporaryDraggingValue,
                      variant,
                    })
                  }
                  propertyState={borderStyleState}
                  comboboxOptions={borderStyleProperties}
                  tooltipContent="Border style"
                  onComboboxSelected={(option, variant) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BORDER_STYLE,
                      setTemporaryDraggingValue:
                        borderStyleState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  onClear={(variant?: string) => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.BORDER_STYLE,
                      setTemporaryDraggingValue:
                        borderStyleState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                />
              </div>
              <div className="col-span-2">
                <div className="text-xs text-secondary-800 font-medium">
                  Width
                </div>
              </div>
              <div className="col-span-3">
                <DraggableInput
                  onSliderValueUpdate={(option, variant) =>
                    updateInstantForProperty({
                      propertyType: PropertyType.BORDER_WIDTH,
                      option,
                      setTemporaryDraggingValue:
                        borderWidthState.setTemporaryDraggingValue,
                      variant,
                    })
                  }
                  labelContent={
                    <svg
                      width="16px"
                      height="16px"
                      viewBox="0 0 16 16"
                      className="bi bi-border-width rotate-180"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M0 3.5A.5.5 0 0 1 .5 3h15a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5H.5a.5.5 0 0 1-.5-.5v-2zm0 5A.5.5 0 0 1 .5 8h15a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H.5a.5.5 0 0 1-.5-.5v-1zm0 4a.5.5 0 0 1 .5-.5h15a.5.5 0 0 1 0 1H.5a.5.5 0 0 1-.5-.5z" />
                    </svg>
                  }
                  min={0}
                  step={0.25}
                  propertyState={borderWidthState}
                  buildArbitraryClass={(input) => ({
                    tailwind: `border-[${input}]`,
                    css: `border-width: ${input};`,
                  })}
                  comboboxOptions={borderWidthProperties}
                  tooltipContent="Border width"
                  onComboboxSelected={(option, variant) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BORDER_WIDTH,
                      setTemporaryDraggingValue:
                        borderWidthState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  disabled={borderWidthExpanded}
                  onClear={(variant?: string) => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.BORDER_WIDTH,
                      setTemporaryDraggingValue:
                        borderWidthState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                />
              </div>
              <div className="col-span-3">
                <DiscreteInput
                  options={BORDER_WIDTH_OPTIONS}
                  selected={borderWidthExpanded ? "individual" : "all"}
                  onOptionSelected={(option) => {
                    if (option.key === "all") {
                      if (borderWidthExpanded) {
                        setBorderWidthExpanded(false);
                        // setBorderWidth(borderLeftWidth);
                        // changeBorderWidth({
                        //   "border-width": borderLeftWidth,
                        // });
                      }
                    } else {
                      if (!borderWidthExpanded) {
                        setBorderWidthExpanded(true);
                      }
                    }
                  }}
                  set={false}
                />
              </div>
              {borderWidthExpanded && (
                <>
                  <div className="col-span-3 col-start-3">
                    <DraggableInput
                      onSliderValueUpdate={(option, variant) =>
                        updateInstantForProperty({
                          propertyType: PropertyType.BORDER_LEFT_WIDTH,
                          option,
                          setTemporaryDraggingValue:
                            borderLeftWidthState.setTemporaryDraggingValue,
                          variant,
                        })
                      }
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BORDER_LEFT_WIDTH,
                          setTemporaryDraggingValue:
                            borderLeftWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      labelContent={<BorderLeftIcon />}
                      min={0}
                      step={0.25}
                      propertyState={borderLeftWidthState}
                      buildArbitraryClass={(input) => ({
                        tailwind: `border-l-[${input}]`,
                        css: `border-left-width: ${input};`,
                      })}
                      tooltipContent="Border left width"
                      comboboxOptions={borderLeftWidthProperties}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BORDER_LEFT_WIDTH,
                          setTemporaryDraggingValue:
                            borderLeftWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                    />
                  </div>
                  <div className="col-span-3">
                    <DraggableInput
                      onSliderValueUpdate={(option, variant) =>
                        updateInstantForProperty({
                          propertyType: PropertyType.BORDER_RIGHT_WIDTH,
                          option,
                          setTemporaryDraggingValue:
                            borderRightWidthState.setTemporaryDraggingValue,
                          variant,
                        })
                      }
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BORDER_RIGHT_WIDTH,
                          setTemporaryDraggingValue:
                            borderRightWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      labelContent={<BorderRightIcon />}
                      min={0}
                      step={0.25}
                      propertyState={borderRightWidthState}
                      buildArbitraryClass={(input) => ({
                        tailwind: `border-r-[${input}]`,
                        css: `border-right-width: ${input};`,
                      })}
                      tooltipContent="Border right width"
                      comboboxOptions={borderRightWidthProperties}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BORDER_RIGHT_WIDTH,
                          setTemporaryDraggingValue:
                            borderRightWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                    />
                  </div>
                  <div className="col-span-3 col-start-3">
                    <DraggableInput
                      onSliderValueUpdate={(option, variant) =>
                        updateInstantForProperty({
                          propertyType: PropertyType.BORDER_BOTTOM_WIDTH,
                          option,
                          setTemporaryDraggingValue:
                            borderBottomWidthState.setTemporaryDraggingValue,
                          variant,
                        })
                      }
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BORDER_BOTTOM_WIDTH,
                          setTemporaryDraggingValue:
                            borderBottomWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      labelContent={<BorderBottomIcon />}
                      min={0}
                      step={0.25}
                      propertyState={borderBottomWidthState}
                      buildArbitraryClass={(input) => ({
                        tailwind: `border-b-[${input}]`,
                        css: `border-bottom-width: ${input};`,
                      })}
                      tooltipContent="Border bottom width"
                      comboboxOptions={borderBottomWidthProperties}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BORDER_BOTTOM_WIDTH,
                          setTemporaryDraggingValue:
                            borderBottomWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                    />
                  </div>
                  <div className="col-span-3">
                    <DraggableInput
                      onSliderValueUpdate={(option, variant) =>
                        updateInstantForProperty({
                          propertyType: PropertyType.BORDER_TOP_WIDTH,
                          option,
                          setTemporaryDraggingValue:
                            borderTopWidthState.setTemporaryDraggingValue,
                          variant,
                        })
                      }
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.BORDER_TOP_WIDTH,
                          setTemporaryDraggingValue:
                            borderTopWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      labelContent={<BorderTopIcon />}
                      min={0}
                      step={0.25}
                      propertyState={borderTopWidthState}
                      buildArbitraryClass={(input) => ({
                        tailwind: `border-t-[${input}]`,
                        css: `border-top-width: ${input};`,
                      })}
                      tooltipContent="Border top width"
                      comboboxOptions={borderTopWidthProperties}
                      onClear={(variant?: string) => {
                        clearTailwindClassForProperty({
                          propertyType: PropertyType.BORDER_TOP_WIDTH,
                          setTemporaryDraggingValue:
                            borderTopWidthState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          {modal.type === StylingModalType.Fill && (
            <FillModalV2
              pickTailwindClassForProperty={pickTailwindClassForProperty}
              updateInstantForProperty={updateInstantForProperty}
              removeClassForProperty={clearTailwindClassForProperty}
              removeClassesForMultipleProperties={
                clearTailwindClassForMultipleProperties
              }
              cssValues={cssValues}
              selectedCodebaseId={selectedCodebaseId}
              classList={elementClasses}
              onOpenFillVariantModal={() => setFillColorVariantModalOpen(true)}
              fillVariantAnchorRef={fillColorVariantAnchorRef}
              modalPopoverRef={fillModalPopoverRef}
              onOpenFromVariantModal={() => setFromColorVariantModalOpen(true)}
              fromVariantAnchorRef={fromColorVariantAnchorRef}
              onOpenViaVariantModal={() => setViaColorVariantModalOpen(true)}
              viaVariantAnchorRef={viaColorVariantAnchorRef}
              onOpenToVariantModal={() => setToColorVariantModalOpen(true)}
              toVariantAnchorRef={toColorVariantAnchorRef}
              onCloseModal={() => setModal({ type: StylingModalType.None })}
            />
          )}
        </ComboboxContainer>
      </ModalContainer>
      {textColorVariantModalOpen && (
        <VariantControl
          valueByVariant={textColorState.valueByVariant}
          onVariantChange={(variant, value) => {
            const option = textColorProperties.find(
              (opt) => opt.tailwind === value,
            );
            if (option) {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.TEXT_COLOR,
                setTemporaryDraggingValue:
                  textColorState.setTemporaryDraggingValue,
                variant,
              });
            }
            setTextColorVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            clearTailwindClassForProperty({
              propertyType: PropertyType.TEXT_COLOR,
              setTemporaryDraggingValue:
                textColorState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="text-color"
          anchorRef={textColorVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={textColorVariantModalOpen}
          setModalOpen={setTextColorVariantModalOpen}
        >
          {(variant, expanded, setExpanded) => {
            if (!expanded || !variant) return null;
            // Build a propertyState for the selected variant
            const variantValue =
              textColorState.valueByVariant[variant]?.value || "";
            const variantDisplayValue =
              textColorState.valueByVariant[variant]?.displayValue || "";
            const variantIsSet =
              !!textColorState.valueByVariant[variant]?.active;
            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              const arbitraryMatch = variantValue.match(
                /^text-\[([#a-fA-F0-9]+)\]$/,
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }
            const variantPropertyState = {
              ...textColorState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };
            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={(input: string) => {
                    return {
                      tailwind: `text-[${input}]`,
                      css: `color: ${input};`,
                    };
                  }}
                  instantUpdateValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.TEXT_COLOR,
                      setTemporaryDraggingValue:
                        textColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  comboboxOptions={sortTailwindColorOptions(
                    textColorProperties,
                  )}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.TEXT_COLOR,
                      setTemporaryDraggingValue:
                        textColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  buildOptionPreview={buildTextColorPreview}
                  tooltipContent="Text color"
                  onClear={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.TEXT_COLOR,
                      setTemporaryDraggingValue:
                        textColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      )}
      {borderColorVariantModalOpen && (
        <VariantControl
          valueByVariant={borderColorState.valueByVariant}
          onVariantChange={(variant, value) => {
            const option = borderColorProperties.find(
              (opt) => opt.tailwind === value,
            );
            if (option) {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.BORDER_COLOR,
                setTemporaryDraggingValue:
                  borderColorState.setTemporaryDraggingValue,
                variant,
              });
            }
            setBorderColorVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            clearTailwindClassForProperty({
              propertyType: PropertyType.BORDER_COLOR,
              setTemporaryDraggingValue:
                borderColorState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="text-color"
          anchorRef={borderColorVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={borderColorVariantModalOpen}
          setModalOpen={setBorderColorVariantModalOpen}
        >
          {(variant, expanded, setExpanded) => {
            if (!expanded || !variant) return null;
            // Build a propertyState for the selected variant
            const variantValue =
              borderColorState.valueByVariant[variant]?.value || "";
            const variantDisplayValue =
              borderColorState.valueByVariant[variant]?.displayValue || "";
            const variantIsSet =
              !!borderColorState.valueByVariant[variant]?.active;
            // Use displayValue if it's a valid hex, otherwise extract from Tailwind class
            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              const arbitraryMatch = variantValue.match(
                /^border-\[([#a-fA-F0-9]+)\]$/,
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }
            const variantPropertyState = {
              ...borderColorState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };
            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={(input: string) => {
                    const baseOption = {
                      tailwind: `border-[${input}]`,
                      css: `border-color: ${input};`,
                    };
                    return convertOptionCssVariableToTailwindClass(baseOption);
                  }}
                  instantUpdateValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BORDER_COLOR,
                      setTemporaryDraggingValue:
                        borderColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  comboboxOptions={sortTailwindColorOptions(
                    borderColorProperties,
                  )}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BORDER_COLOR,
                      setTemporaryDraggingValue:
                        borderColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  tooltipContent="Border color"
                  onClear={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.BORDER_COLOR,
                      setTemporaryDraggingValue:
                        borderColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      )}
      {fillColorVariantModalOpen && (
        <VariantControl
          valueByVariant={backgroundColorState.valueByVariant}
          onVariantChange={(variant, value) => {
            const option = backgroundColorProperties.find(
              (opt) => opt.tailwind === value,
            );
            if (option) {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.BACKGROUND_COLOR,
                setTemporaryDraggingValue:
                  backgroundColorState.setTemporaryDraggingValue,
                variant,
              });
            }
            setFillColorVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            clearTailwindClassForProperty({
              propertyType: PropertyType.BACKGROUND_COLOR,
              setTemporaryDraggingValue:
                backgroundColorState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="text-color"
          anchorRef={fillColorVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={fillColorVariantModalOpen}
          setModalOpen={setFillColorVariantModalOpen}
        >
          {(variant, expanded, setExpanded) => {
            if (!expanded || !variant) return null;
            const variantValue =
              backgroundColorState.valueByVariant[variant]?.value || "";
            const variantDisplayValue =
              backgroundColorState.valueByVariant[variant]?.displayValue || "";
            const variantIsSet =
              !!backgroundColorState.valueByVariant[variant]?.active;
            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              const arbitraryMatch = variantValue.match(
                /^bg-\[([#a-fA-F0-9]+)\]$/,
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }
            const variantPropertyState = {
              ...backgroundColorState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };
            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={(input: string) => {
                    const baseOption = {
                      tailwind: `bg-[${input}]`,
                      css: `background-color: ${input}`,
                    };
                    return convertOptionCssVariableToTailwindClass(baseOption);
                  }}
                  instantUpdateValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BACKGROUND_COLOR,
                      setTemporaryDraggingValue:
                        backgroundColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  comboboxOptions={sortTailwindColorOptions(
                    backgroundColorProperties,
                  )}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BACKGROUND_COLOR,
                      setTemporaryDraggingValue:
                        backgroundColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  tooltipContent="Background color"
                  onClear={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.BACKGROUND_COLOR,
                      setTemporaryDraggingValue:
                        backgroundColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      )}
      {fromColorVariantModalOpen && (
        <VariantControl
          valueByVariant={fromColorState.valueByVariant}
          onVariantChange={(variant, value) => {
            const option = fromColorProperties.find(
              (opt) => opt.tailwind === value,
            );
            if (option) {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.FROM_COLOR,
                setTemporaryDraggingValue:
                  fromColorState.setTemporaryDraggingValue,
                variant,
              });
            }
            setFromColorVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            clearTailwindClassForProperty({
              propertyType: PropertyType.FROM_COLOR,
              setTemporaryDraggingValue:
                fromColorState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="text-color"
          anchorRef={fromColorVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={fromColorVariantModalOpen}
          setModalOpen={setFromColorVariantModalOpen}
        >
          {(variant, expanded, setExpanded) => {
            if (!expanded || !variant) return null;
            const variantValue =
              fromColorState.valueByVariant[variant]?.value || "";
            const variantDisplayValue =
              fromColorState.valueByVariant[variant]?.displayValue || "";
            const variantIsSet =
              !!fromColorState.valueByVariant[variant]?.active;
            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              const arbitraryMatch = variantValue.match(
                /^from-\[([#a-fA-F0-9]+)\]$/,
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }
            const variantPropertyState = {
              ...fromColorState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };
            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={(input: string) => ({
                    tailwind: `from-[${input}]`,
                    css: `--tw-gradient-from: ${input} var(--tw-gradient-from-position);`,
                  })}
                  instantUpdateValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.FROM_COLOR,
                      setTemporaryDraggingValue:
                        fromColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  comboboxOptions={sortTailwindColorOptions(
                    fromColorProperties,
                  )}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.FROM_COLOR,
                      setTemporaryDraggingValue:
                        fromColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  tooltipContent="From color"
                  onClear={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.FROM_COLOR,
                      setTemporaryDraggingValue:
                        fromColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      )}
      {viaColorVariantModalOpen && (
        <VariantControl
          valueByVariant={viaColorState.valueByVariant}
          onVariantChange={(variant, value) => {
            const option = viaColorProperties.find(
              (opt) => opt.tailwind === value,
            );
            if (option) {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.VIA_COLOR,
                setTemporaryDraggingValue:
                  viaColorState.setTemporaryDraggingValue,
                variant,
              });
            }
            setViaColorVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            clearTailwindClassForProperty({
              propertyType: PropertyType.VIA_COLOR,
              setTemporaryDraggingValue:
                viaColorState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="text-color"
          anchorRef={viaColorVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={viaColorVariantModalOpen}
          setModalOpen={setViaColorVariantModalOpen}
        >
          {(variant, expanded, setExpanded) => {
            if (!expanded || !variant) return null;
            const variantValue =
              viaColorState.valueByVariant[variant]?.value || "";
            const variantDisplayValue =
              viaColorState.valueByVariant[variant]?.displayValue || "";
            const variantIsSet =
              !!viaColorState.valueByVariant[variant]?.active;
            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              const arbitraryMatch = variantValue.match(
                /^via-\[([#a-fA-F0-9]+)\]$/,
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }
            const variantPropertyState = {
              ...viaColorState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };
            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={(input: string) => ({
                    tailwind: `via-[${input}]`,
                    css: `--tw-gradient-stops: var(--tw-gradient-from), ${input} var(--tw-gradient-via-position), var(--tw-gradient-to);`,
                  })}
                  instantUpdateValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.VIA_COLOR,
                      setTemporaryDraggingValue:
                        viaColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  comboboxOptions={sortTailwindColorOptions(viaColorProperties)}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.VIA_COLOR,
                      setTemporaryDraggingValue:
                        viaColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  tooltipContent="Via color"
                  onClear={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.VIA_COLOR,
                      setTemporaryDraggingValue:
                        viaColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      )}
      {toColorVariantModalOpen && (
        <VariantControl
          valueByVariant={toColorState.valueByVariant}
          onVariantChange={(variant, value) => {
            const option = toColorProperties.find(
              (opt) => opt.tailwind === value,
            );
            if (option) {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.TO_COLOR,
                setTemporaryDraggingValue:
                  toColorState.setTemporaryDraggingValue,
                variant,
              });
            }
            setToColorVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            clearTailwindClassForProperty({
              propertyType: PropertyType.TO_COLOR,
              setTemporaryDraggingValue: toColorState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="text-color"
          anchorRef={toColorVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={toColorVariantModalOpen}
          setModalOpen={setToColorVariantModalOpen}
        >
          {(variant, expanded, setExpanded) => {
            if (!expanded || !variant) return null;
            const variantValue =
              toColorState.valueByVariant[variant]?.value || "";
            const variantDisplayValue =
              toColorState.valueByVariant[variant]?.displayValue || "";
            const variantIsSet = !!toColorState.valueByVariant[variant]?.active;
            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              const arbitraryMatch = variantValue.match(
                /^to-\[([#a-fA-F0-9]+)\]$/,
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }
            const variantPropertyState = {
              ...toColorState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };
            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={(input: string) => ({
                    tailwind: `to-[${input}]`,
                    css: `--tw-gradient-to: ${input} var(--tw-gradient-to-position);`,
                  })}
                  instantUpdateValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.TO_COLOR,
                      setTemporaryDraggingValue:
                        toColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  comboboxOptions={sortTailwindColorOptions(toColorProperties)}
                  setValue={(option) => {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.TO_COLOR,
                      setTemporaryDraggingValue:
                        toColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  tooltipContent="To color"
                  onClear={() => {
                    clearTailwindClassForProperty({
                      propertyType: PropertyType.TO_COLOR,
                      setTemporaryDraggingValue:
                        toColorState.setTemporaryDraggingValue,
                      variant,
                    });
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      )}
    </div>
  );
};
